import { useCall } from '../contexts/CallContext';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { IconPhone, IconVideo, IconTrash, IconEdit, IconReply, IconStar, IconSettings, IconCamera, IconUsers, IconPlus, IconSmile, IconSticker, IconMic, IconPaperclip, IconSend } from '../components/Icons';
import api from '../services/api';
import AppleEmoji from '../components/Emoji';
import toast from 'react-hot-toast';
import { io, Socket } from 'socket.io-client';
import EmojiPicker, { Emoji, EmojiStyle, Theme } from 'emoji-picker-react';

// Mapeo explícito para emojis especiales y de reacciones rápidas de WhatsApp
const EMOJI_SPECIAL_UNIFIED_MAP: Record<string, string> = {
  '❤️': '2764-fe0f',
  '❤': '2764-fe0f',
  '⭐': '2b50',
  '✨': '2728',
  '⚡': '26a1',
  '🔥': '1f525',
  '👍': '1f44d',
  '👎': '1f44e',
  '😂': '1f602',
  '😮': '1f62e',
  '😢': '1f622',
  '🙏': '1f64f',
  '🎉': '1f389',
  '👏': '1f44f',
  '🚀': '1f680',
  '💯': '1f4af',
  '👌': '1f44c',
  '🤝': '1f91d',
  '💪': '1f4aa',
  '🥳': '1f973',
  '😎': '1f60e',
  '💡': '1f4a1',
  '😍': '1f60d',
  '🥰': '1f970',
};

// Conversor de Caracter Emoji a Código Unificado para WhatsApp / Apple Emojis
const emojiToUnified = (emoji: string): string => {
  if (!emoji) return '';
  if (EMOJI_SPECIAL_UNIFIED_MAP[emoji]) return EMOJI_SPECIAL_UNIFIED_MAP[emoji];

  const codePoints: string[] = [];
  for (const char of emoji) {
    const cp = char.codePointAt(0);
    if (cp) {
      codePoints.push(cp.toString(16).toLowerCase());
    }
  }
  return codePoints.join('-');
};

// Componente para renderizar cualquier emoji en estilo oficial WhatsApp / Apple con fallback automático
const WhatsAppEmoji: React.FC<{ emoji: string; size?: number; style?: React.CSSProperties }> = ({ emoji, size = 18, style }) => {
  const unified = React.useMemo(() => emojiToUnified(emoji), [emoji]);
  if (!unified) return <span>{emoji}</span>;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', width: size, height: size, lineHeight: 1, flexShrink: 0, ...style }}>
      <img
        src={`https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${unified}.png`}
        alt={emoji}
        width={size}
        height={size}
        style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain', display: 'inline-block' }}
        onError={(e) => {
          const target = e.currentTarget;
          if (unified.includes('-fe0f')) {
            target.src = `https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${unified.replace('-fe0f', '')}.png`;
          } else if (!target.dataset.triedFe0f) {
            target.dataset.triedFe0f = 'true';
            target.src = `https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${unified}-fe0f.png`;
          } else {
            target.style.display = 'none';
            if (target.parentElement) target.parentElement.innerText = emoji;
          }
        }}
      />
    </span>
  );
};

const AC = ['#0070f3', '#2688c9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];
const colorFor = (s: string = '') => AC[(s.charCodeAt(0) || 65) % AC.length];
const ini = (s: string = '') => s.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase();
const fmtT = (d: string) => new Date(d).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
const fmtD = (d: string) => {
  const dt = new Date(d), now = new Date(), diff = Math.floor((now.getTime() - dt.getTime()) / 86400000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return dt.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
};

// Generador de Tono de Mensaje
const playPing = () => {
  try {
    const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
    [[880, 0], [1109, 0.12]].forEach(([f, t]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = f as number;
      g.gain.setValueAtTime(0, ctx.currentTime + (t as number));
      g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + (t as number) + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (t as number) + 0.2);
      o.start(ctx.currentTime + (t as number)); o.stop(ctx.currentTime + (t as number) + 0.25);
    });
  } catch {}
};

const getUser = () => {
  try {
    const t = localStorage.getItem('token') || '';
    if (t) {
      const p = JSON.parse(atob(t.split('.')[1]));
      const uid = p.userId || p.id || p.sub || p._id || '';
      if (uid) return { ...p, userId: uid, id: uid };
    }
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const uid2 = u.userId || u.id || u._id || u.sub || '';
    return { ...u, userId: uid2, id: uid2 };
  } catch { return {}; }
};

const EMOJIS: Record<string, string[]> = {
  '😀': ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😗','😋','😜','😎','🤓','🥳','🤔','🤫','🤗'],
  '👋': ['👍','👎','👏','🙌','👐','🤝','🙏','✌','🤞','🤙','💪','🔥','✨','⭐','🎉','🚀','💯','❤️','💙','💜','🖤'],
  '💼': ['📁','📎','📌','📝','📊','📈','💡','🎯','🏆','📅','⏰','💬','📞','🎥','🎨','⚙️','🔑','🔒']
};

// Ícono SVG de Doble Check estilo WhatsApp / Mensajería
export const DoubleCheckIcon: React.FC<{ size?: number; color?: string; style?: React.CSSProperties }> = ({ size = 15, color = 'currentColor', style }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', lineHeight: 1, ...style }}>
    <svg width={size} height={Math.round(size * 0.7)} viewBox="0 0 16 11" fill="none" style={{ display: 'inline-block' }}>
      <path d="M11.0001 1.5L4.50006 8L1.50006 5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.0001 1.5L8.50006 8L7.00006 6.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

// Ícono SVG de Descarga Universal
export const IconDownload: React.FC<{ size?: number; color?: string; style?: React.CSSProperties }> = ({ size = 16, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Formateador universal de tamaño de archivos
export const formatFileSize = (bytes?: number) => {
  if (!bytes || isNaN(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Resolver inteligente de tipo de archivo, icono y color
export const getFileMeta = (fileName: string = '', fileType?: string) => {
  const ext = (fileName.split('.').pop() || '').toLowerCase();

  // 1. ARCHIVOS DE DISEÑO Y EDICIÓN PROFESIONAL (ADOBE, COREL, FIGMA, 3D, RAW)
  if (['psd', 'psb'].includes(ext)) {
    return { type: 'design_psd', icon: '🎨', color: '#31a8ff', badge: ext.toUpperCase(), isMedia: false, label: 'Adobe Photoshop' };
  }
  if (['ai'].includes(ext)) {
    return { type: 'design_ai', icon: '🖌️', color: '#ff9a00', badge: 'AI', isMedia: false, label: 'Adobe Illustrator' };
  }
  if (['eps', 'ps'].includes(ext)) {
    return { type: 'design_eps', icon: '📐', color: '#e11d48', badge: ext.toUpperCase(), isMedia: false, label: 'Vector PostScript' };
  }
  if (['indd', 'idml'].includes(ext)) {
    return { type: 'design_indesign', icon: '📑', color: '#ff3366', badge: ext.toUpperCase(), isMedia: false, label: 'Adobe InDesign' };
  }
  if (['prproj'].includes(ext)) {
    return { type: 'design_premiere', icon: '🎬', color: '#ea77ff', badge: 'PR', isMedia: false, label: 'Adobe Premiere' };
  }
  if (['aep', 'aepx'].includes(ext)) {
    return { type: 'design_ae', icon: '✨', color: '#9999ff', badge: 'AE', isMedia: false, label: 'After Effects' };
  }
  if (['cdr'].includes(ext)) {
    return { type: 'design_cdr', icon: '🎯', color: '#00c853', badge: 'CDR', isMedia: false, label: 'CorelDRAW' };
  }
  if (['fig', 'sketch', 'xd'].includes(ext)) {
    return { type: 'design_ui', icon: '🎨', color: '#a855f7', badge: ext.toUpperCase(), isMedia: false, label: 'Diseño UI/UX' };
  }
  if (['blend', 'c4d', 'obj', 'fbx', '3ds', 'stl', 'max', 'ma', 'mb'].includes(ext)) {
    return { type: 'design_3d', icon: '🧊', color: '#06b6d4', badge: ext.toUpperCase(), isMedia: false, label: 'Modelo 3D' };
  }
  if (['raw', 'cr2', 'cr3', 'nef', 'arw', 'dng', 'rw2', 'orf', 'tiff', 'tif'].includes(ext)) {
    return { type: 'camera_raw', icon: '📷', color: '#f59e0b', badge: ext.toUpperCase(), isMedia: false, label: 'Fotografía RAW' };
  }
  if (['svg'].includes(ext)) {
    return { type: 'vector_svg', icon: '📐', color: '#f59e0b', badge: 'SVG', isMedia: false, label: 'Vector SVG' };
  }
  if (['psd', 'ai', 'fig', 'sketch', 'xd', 'eps'].includes(ext) || fileType === 'design') {
    return { type: 'design', icon: '🎨', color: '#ec4899', badge: ext.toUpperCase() || 'DESIGN', isMedia: false, label: 'Archivo de Diseño' };
  }

  // 2. IMÁGENES WEB ESTÁNDAR
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic', 'bmp', 'ico'].includes(ext) || fileType === 'image') {
    return { type: 'image', icon: '🖼️', color: '#0070f3', badge: ext.toUpperCase() || 'IMG', isMedia: true, label: 'Imagen' };
  }
  // 3. VIDEOS
  if (['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v', '3gp'].includes(ext) || fileType === 'video') {
    return { type: 'video', icon: '🎥', color: '#8b5cf6', badge: ext.toUpperCase() || 'VID', isMedia: true, label: 'Video' };
  }
  // 4. AUDIOS
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'opus', 'flac'].includes(ext) || fileType === 'audio') {
    return { type: 'audio', icon: '🎵', color: '#10b981', badge: ext.toUpperCase() || 'AUD', isMedia: false, label: 'Audio' };
  }
  // 5. PDF
  if (['pdf'].includes(ext) || fileType === 'pdf') {
    return { type: 'pdf', icon: '📕', color: '#ef4444', badge: 'PDF', isMedia: false, label: 'Documento PDF' };
  }
  // 6. DOCUMENTOS DE TEXTO
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'pages'].includes(ext) || fileType === 'document') {
    return { type: 'document', icon: '📘', color: '#2563eb', badge: ext.toUpperCase() || 'DOC', isMedia: false, label: 'Documento' };
  }
  // 7. HOJAS DE CÁLCULO
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext) || fileType === 'spreadsheet') {
    return { type: 'spreadsheet', icon: '📊', color: '#059669', badge: ext.toUpperCase() || 'XLS', isMedia: false, label: 'Hoja de Cálculo' };
  }
  // 8. PRESENTACIONES
  if (['ppt', 'pptx', 'key', 'odp'].includes(ext) || fileType === 'presentation') {
    return { type: 'presentation', icon: '📙', color: '#ea580c', badge: ext.toUpperCase() || 'PPT', isMedia: false, label: 'Presentación' };
  }
  // 9. ARCHIVOS COMPRIMIDOS
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'iso'].includes(ext) || fileType === 'archive') {
    return { type: 'archive', icon: '🗜️', color: '#d97706', badge: ext.toUpperCase() || 'ZIP', isMedia: false, label: 'Archivo Comprimido' };
  }
  // 10. CÓDIGO FUENTE
  if (['js', 'ts', 'tsx', 'jsx', 'json', 'html', 'css', 'py', 'sql', 'cpp', 'c', 'php', 'java', 'dart', 'sh'].includes(ext) || fileType === 'code') {
    return { type: 'code', icon: '💻', color: '#6366f1', badge: ext.toUpperCase() || 'CODE', isMedia: false, label: 'Código' };
  }

  return { type: 'generic', icon: '📄', color: '#64748b', badge: ext.toUpperCase() || 'FILE', isMedia: false, label: 'Archivo' };
};

// Descargador universal de archivos multiplataforma (PC, Android, iOS)
export const downloadFile = async (url: string, filename?: string) => {
  if (!url) return;
  const safeName = filename || url.split('/').pop() || 'archivo_zoik';
  const toastId = toast.loading(`Descargando ${safeName}...`);
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('Error de red');
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 400);
    toast.success('Descarga completada', { id: toastId });
  } catch (err) {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Abriendo descarga...', { id: toastId });
    } catch {
      window.open(url, '_blank');
      toast.dismiss(toastId);
    }
  }
};

// Verifica si un mensaje aún está dentro del tiempo de prudencia para edición (3 horas desde su envío)
export const isMessageEditable = (createdAt?: string | Date): boolean => {
  if (!createdAt) return false;
  const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
  const msgTime = new Date(createdAt).getTime();
  if (isNaN(msgTime)) return false;
  return (Date.now() - msgTime) <= THREE_HOURS_MS;
};

// ==========================================
// ANIMATED STATUS ICONS (Zoik Custom Statuses)
// ==========================================

export const StatusGear: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#10b981' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, flexShrink: 0 }}>
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        animation: 'zoikGearSpin 3.5s linear infinite',
        transformOrigin: '50% 50%'
      }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  </span>
);

export const StatusZzz: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#f59e0b' }) => (
  <span style={{
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    flexShrink: 0
  }}>
    <span
      style={{
        position: 'absolute',
        bottom: '1px',
        left: '1px',
        fontSize: `${Math.max(9, Math.round(size * 0.58))}px`,
        fontWeight: 900,
        color: color,
        lineHeight: 1,
        fontFamily: '"Montserrat", system-ui, sans-serif',
        animation: 'zoikZzzFloat1 2s infinite ease-in-out'
      }}
    >
      Z
    </span>
    <span
      style={{
        position: 'absolute',
        bottom: '4px',
        left: '5px',
        fontSize: `${Math.max(8, Math.round(size * 0.48))}px`,
        fontWeight: 900,
        color: color,
        lineHeight: 1,
        fontFamily: '"Montserrat", system-ui, sans-serif',
        animation: 'zoikZzzFloat2 2s infinite ease-in-out 0.4s'
      }}
    >
      z
    </span>
    <span
      style={{
        position: 'absolute',
        top: '0px',
        right: '1px',
        fontSize: `${Math.max(7, Math.round(size * 0.38))}px`,
        fontWeight: 900,
        color: color,
        lineHeight: 1,
        fontFamily: '"Montserrat", system-ui, sans-serif',
        animation: 'zoikZzzFloat3 2s infinite ease-in-out 0.8s'
      }}
    >
      z
    </span>
  </span>
);

export const StatusPacman: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#f97316' }) => (
  <span style={{
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    width: size + 7,
    height: size,
    flexShrink: 0
  }}>
    <svg width={size} height={size} viewBox="0 0 26 26" style={{ overflow: 'visible', flexShrink: 0 }}>
      {/* Parte trasera fija (semicírculo izquierdo estático) */}
      <path
        d="M 13 1 A 12 12 0 0 0 13 25 Z"
        fill={color}
      />
      {/* Mandíbula Superior (solo se abre hacia arriba en el lado derecho) */}
      <path
        d="M 13 13 L 25 13 A 12 12 0 0 0 13 1 Z"
        fill={color}
        style={{
          transformOrigin: '13px 13px',
          animation: 'zoikPacmanChompTopOnly 0.32s infinite ease-in-out alternate'
        }}
      />
      {/* Mandíbula Inferior (solo se abre hacia abajo en el lado derecho) */}
      <path
        d="M 13 13 L 13 25 A 12 12 0 0 0 25 13 Z"
        fill={color}
        style={{
          transformOrigin: '13px 13px',
          animation: 'zoikPacmanChompBottomOnly 0.32s infinite ease-in-out alternate'
        }}
      />
      {/* Ojo */}
      <circle cx="10" cy="7" r="1.8" fill="#ffffff" />
      <circle cx="10.8" cy="7" r="0.9" fill="#0f2a4a" />
    </svg>
    {/* Galleta / Pellet moviéndose desde la derecha hacia la boca */}
    <span
      style={{
        position: 'absolute',
        right: '0px',
        top: '50%',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        background: '#fbbf24',
        boxShadow: '0 0 4px #fbbf24',
        transform: 'translateY(-50%)',
        animation: 'zoikPacmanCookieMove 0.64s infinite linear'
      }}
    />
  </span>
);

export const StatusUnplugged: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#ef4444' }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size + 4,
    height: size,
    flexShrink: 0
  }}>
    <svg width={size + 4} height={size} viewBox="0 0 28 20" fill="none" style={{ overflow: 'visible' }}>
      {/* Tomacorriente / Hembra (Lado izquierdo que retrocede) */}
      <g style={{ animation: 'zoikSocketPull 2s ease-in-out infinite alternate', transformOrigin: '7px 10px' }}>
        <rect x="2" y="4.5" width="6.5" height="11" rx="2" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.8" />
        <line x1="0" y1="10" x2="2" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round" />
        {/* Ranuras hembra */}
        <line x1="6.5" y1="7.5" x2="8.5" y2="7.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
        <line x1="6.5" y1="12.5" x2="8.5" y2="12.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      </g>
      
      {/* Enchufe / Macho con clavijas (Lado derecho que se desconecta) */}
      <g style={{ animation: 'zoikPlugDisconnect 2s ease-in-out infinite alternate', transformOrigin: '20px 10px' }}>
        {/* Clavijas metálicas expuestas */}
        <line x1="10.5" y1="7.5" x2="15.5" y2="7.5" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="10.5" y1="12.5" x2="15.5" y2="12.5" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" />
        {/* Cuerpo del enchufe */}
        <rect x="15.5" y="4.5" width="7" height="11" rx="2" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.8" />
        <line x1="22.5" y1="10" x2="26" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </g>
      
      {/* Destello / Chispa en la desconexión */}
      <circle cx="10" cy="10" r="1.5" fill={color} style={{ animation: 'zoikSparkPulse 2s ease-in-out infinite alternate' }} />
    </svg>
  </span>
);

export const StatusIndicator: React.FC<{ status: string; size?: number }> = ({ status, size = 16 }) => {
  switch (status) {
    case 'disponible':
    case 'online':
      return <StatusGear size={size} color="#10b981" />;
    case 'ausente':
    case 'away':
      return <StatusZzz size={size} color="#f59e0b" />;
    case 'almorzando':
    case 'lunch':
      return <StatusPacman size={size} color="#f97316" />;
    case 'ocupado':
    case 'desconectado':
    case 'no_disponible':
    case 'dnd':
      return <StatusUnplugged size={size} color="#ef4444" />;
    default:
      return <StatusGear size={size} color="#10b981" />;
  }
};

const USER_STATUSES = [
  { id: 'disponible', label: 'Disponible', color: '#10b981' },
  { id: 'ausente', label: 'Ausente', color: '#f59e0b' },
  { id: 'almorzando', label: 'Almorzando', color: '#f97316' },
  { id: 'ocupado', label: 'Desconectado', color: '#ef4444' }
];


// Cache local de previews
const linkPreviewCache: Record<string, any> = {};

const MessageLinkPreviewCard: React.FC<{ url: string; isMe: boolean }> = ({ url, isMe }) => {
  const [meta, setMeta] = useState<any>(linkPreviewCache[url] || null);

  useEffect(() => {
    if (linkPreviewCache[url]) {
      setMeta(linkPreviewCache[url]);
      return;
    }
    api.get(`/chat/link-preview?url=${encodeURIComponent(url)}`).then(res => {
      if (res.data && (res.data.title || res.data.image)) {
        linkPreviewCache[url] = res.data;
        setMeta(res.data);
      }
    }).catch(() => {});
  }, [url]);

  if (!meta || (!meta.title && !meta.image)) return null;

  return (
    <a
      href={meta.url || url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        marginTop: '8px',
        background: isMe ? 'rgba(0, 0, 0, 0.18)' : '#f8fafc',
        border: isMe ? '1px solid rgba(255,255,255,0.25)' : '1.5px solid #cbd5e1',
        borderRadius: '12px',
        overflow: 'hidden',
        textDecoration: 'none',
        color: isMe ? '#ffffff' : '#0f2a4a',
        maxWidth: '320px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      {meta.image && (
        <div style={{ width: '100%', height: '140px', overflow: 'hidden', background: '#000' }}>
          <img src={meta.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <div style={{ padding: '8px 12px' }}>
        <div style={{ fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', color: isMe ? '#93c5fd' : '#0070f3', letterSpacing: '0.3px' }}>
          {meta.siteName || 'WEB'}
        </div>
        <div style={{ fontSize: '12px', fontWeight: 800, marginTop: '2px', lineHeight: 1.3 }}>
          {meta.title}
        </div>
        {meta.description && (
          <div style={{ fontSize: '11px', color: isMe ? 'rgba(255,255,255,0.8)' : '#64748b', marginTop: '2px', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {meta.description}
          </div>
        )}
      </div>
    </a>
  );
};

const EMOJI_AND_MENTION_REGEX = /(@[a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ]+(?:\s+[a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ]+)?|(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})+)/gu;

const renderParsedText = (text: string, isMe: boolean) => {
  if (!text) return null;
  const parts = text.split(EMOJI_AND_MENTION_REGEX);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (part.startsWith('@')) {
      return (
        <span
          key={idx}
          style={{
            background: isMe ? 'rgba(255,255,255,0.25)' : '#dbeafe',
            color: isMe ? '#ffffff' : '#0070f3',
            fontWeight: 800,
            padding: '2px 6px',
            borderRadius: '6px',
            display: 'inline-block'
          }}
        >
          {part}
        </span>
      );
    }

    if (/\p{Extended_Pictographic}/u.test(part)) {
      const emojiChars = Array.from(part);
      return (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', verticalAlign: 'middle', margin: '0 1px' }}>
          {emojiChars.map((em, i) => (
            <WhatsAppEmoji key={i} emoji={em} size={20} />
          ))}
        </span>
      );
    }

    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

const oldRenderParsedText = (text: string, isMe: boolean) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const parts = text.split(urlRegex);

  return (
    <>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                color: isMe ? '#e0f2fe' : '#0070f3',
                fontWeight: 700,
                textDecoration: 'underline',
                wordBreak: 'break-all', whiteSpace: 'pre-wrap'
              }}
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
};

// Componente Reproductor de Notas de Voz estilo WhatsApp Web
export const WebVoiceNotePlayer: React.FC<{
  url: string;
  name?: string;
  isMe?: boolean;
}> = ({ url, name = 'Nota de voz', isMe = false }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isBuffering, setIsBuffering] = useState(false);

  // 30 barras de audio con patrón armónico y estético estilo WhatsApp
  const waveformHeights = React.useMemo(() => {
    let hash = 0;
    for (let i = 0; i < url.length; i++) hash = (hash * 31 + url.charCodeAt(i)) & 0xffffffff;
    const bars: number[] = [];
    for (let i = 0; i < 30; i++) {
      const v = Math.abs(Math.sin((hash % 1000) + i * 0.75) * 0.6 + Math.cos(i * 1.3) * 0.4);
      bars.push(Math.round(25 + v * 75));
    }
    return bars;
  }, [url]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error al reproducir audio:', err);
        toast('Si no escuchas sonido, verifica que tu teléfono no esté en modo silencio', { icon: '🔊' });
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      const dur = audioRef.current.duration;
      if (isFinite(dur) && dur > 0) {
        setDuration(dur);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    let dur = audioRef.current.duration;
    if (dur === Infinity || isNaN(dur)) {
      audioRef.current.currentTime = 1e101;
      audioRef.current.ontimeupdate = function() {
        if (!audioRef.current) return;
        audioRef.current.ontimeupdate = handleTimeUpdate;
        dur = audioRef.current.duration;
        audioRef.current.currentTime = 0;
        if (isFinite(dur) && dur > 0) setDuration(dur);
      };
    } else if (isFinite(dur) && dur > 0) {
      setDuration(dur);
    }
  };

  const handleSeek = (index: number) => {
    if (!audioRef.current || !duration || !isFinite(duration)) return;
    const seekFraction = index / (waveformHeights.length - 1);
    const target = seekFraction * duration;
    audioRef.current.currentTime = target;
    setCurrentTime(target);
  };

  const cycleSpeed = () => {
    const nextSpeed = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    setPlaybackRate(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const formatAudioTime = (sec: number) => {
    if (!isFinite(sec) || isNaN(sec) || sec < 0) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const progress = duration > 0 ? Math.min(Math.max(currentTime / duration, 0), 1) : 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        minWidth: '240px',
        maxWidth: '330px',
        padding: '2px 0'
      }}
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)}
        onPlaying={() => setIsBuffering(false)}
      />

      {/* Botón Circular Play / Pause */}
      <button
        onClick={togglePlay}
        title={isPlaying ? 'Pausar' : 'Reproducir nota de voz'}
        style={{
          width: '42px',
          height: '42px',
          borderRadius: '50%',
          border: 'none',
          background: isMe ? '#ffffff' : 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
          color: isMe ? '#0070f3' : '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
          boxShadow: isMe ? '0 4px 12px rgba(0,0,0,0.18)' : '0 4px 14px rgba(0,112,243,0.35)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease'
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {isBuffering ? (
          <span style={{ width: '16px', height: '16px', border: `2.5px solid ${isMe ? '#0070f3' : '#ffffff'}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'zoikGearSpin 0.7s linear infinite' }} />
        ) : isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="5" y="4" width="4.5" height="16" rx="2" />
            <rect x="14.5" y="4" width="4.5" height="16" rx="2" />
          </svg>
        ) : (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px' }}>
            <path d="M7 5.5v13a1 1 0 0 0 1.55.83l10.5-6.5a1 1 0 0 0 0-1.66l-10.5-6.5A1 1 0 0 0 7 5.5z" />
          </svg>
        )}
      </button>

      {/* Contenedor de Onda + Tiempo + Velocidad */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        {/* Onda de Audio Interactiva con Scrubber */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2.5px',
            height: '28px',
            cursor: 'pointer',
            padding: '2px 0'
          }}
          title="Haz clic para saltar a este punto del audio"
        >
          {waveformHeights.map((h, i) => {
            const barFraction = i / (waveformHeights.length - 1);
            const isActive = barFraction <= progress;
            return (
              <div
                key={i}
                onClick={() => handleSeek(i)}
                style={{
                  flex: 1,
                  height: `${h}%`,
                  minHeight: '4px',
                  borderRadius: '99px',
                  background: isMe
                    ? (isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.42)')
                    : (isActive ? '#0070f3' : 'rgba(15, 42, 74, 0.22)'),
                  transition: 'height 0.1s ease, background 0.12s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.85';
                  e.currentTarget.style.transform = 'scaleY(1.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scaleY(1)';
                }}
              />
            );
          })}
        </div>

        {/* Fila Inferior: Temporizador, Velocidad y Descarga */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: isMe ? '#ffffff' : '#64748b',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.2px'
            }}
          >
            {isPlaying ? formatAudioTime(currentTime) : (duration > 0 ? formatAudioTime(duration) : '0:00')}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Botón de Velocidad 1x / 1.5x / 2x */}
            <button
              onClick={cycleSpeed}
              title="Cambiar velocidad"
              style={{
                padding: '2px 6px',
                borderRadius: '6px',
                border: 'none',
                background: isMe ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 112, 243, 0.1)',
                color: isMe ? '#ffffff' : '#0070f3',
                fontSize: '10px',
                fontWeight: 800,
                cursor: 'pointer',
                lineHeight: 1.2,
                transition: 'background 0.15s ease'
              }}
            >
              {playbackRate}x
            </button>

            {/* Botón Descargar */}
            <button
              onClick={() => downloadFile(url, name || 'audio.mp3')}
              title="Descargar audio"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: 'none',
                background: isMe ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 112, 243, 0.08)',
                color: isMe ? '#ffffff' : '#0070f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <IconDownload size={12} color={isMe ? '#ffffff' : '#0070f3'} />
            </button>

            {/* Micrófono WhatsApp */}
            <IconMic size={13} color={isMe ? '#ffffff' : '#0070f3'} />
          </div>
        </div>
      </div>
    </div>
  );
};


export const Chat: React.FC = () => {
  const { startCall: triggerGlobalCall } = useCall();
  const user = getUser();
  const { user: authUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [myId, setMyId] = useState(user.userId || user.id || '');
  const [chatStatus, setChatStatus] = useState<string>(() => localStorage.getItem('chatStatus') || 'disponible');

  useEffect(() => { api.get('/chat/me').then(r => setMyId(r.data.id)).catch(() => {}); }, []);

  const [convs, setConvs] = useState<any[]>([]);
  const [active, setActive] = useState<any>(null);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState<boolean>(false);
  const [loadingOlder, setLoadingOlder] = useState<boolean>(false);
  const [totalMsgCount, setTotalMsgCount] = useState<number>(0);
  // Estados de Modo Concentración en el Trabajo
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    return localStorage.getItem('zoik_focus_mode_enabled') === 'true';
  });
  const [focusAllowedUserIds, setFocusAllowedUserIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('zoik_focus_allowed_users') || '[]');
    } catch {
      return [];
    }
  });
  const [showFocusModal, setShowFocusModal] = useState<boolean>(false);
  const [focusSearch, setFocusSearch] = useState<string>('');
  const [input, setInput] = useState('');
  // Estados de menciones con @
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState<number>(0);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [activeReactionMsgId, setActiveReactionMsgId] = useState<string | null>(null);
  const [team, setTeam] = useState<any[]>([]);
  const [stickers, setStickers] = useState<any[]>([]);
  const [filter, setFilter] = useState<'all' | 'direct' | 'groups' | 'focus'>('all');
  const [search, setSearch] = useState('');
  const [isChatListCollapsed, setIsChatListCollapsed] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  // Barra Desplegable de Emojis y Stickers
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState<'emoji' | 'stickers'>('emoji');
  const [selectedEmojiCat, setSelectedEmojiCat] = useState('all');
  interface AttachmentItem {
    id: string;
    file: File;
    url: string;
    type: string;
    name: string;
    size: number;
    ext: string;
  }
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [preview, setPreview] = useState<{ file: File, url: string, type: string } | null>(null);
  const [isRec, setIsRec] = useState(false);
  const [recT, setRecT] = useState(0);
  const [sending, setSending] = useState(false);
  const [isFlying, setIsFlying] = useState(false);

  // Estados de Indicador de Typing en Tiempo Real
  const [typingMap, setTypingMap] = useState<Record<string, Record<string, { userName: string; userAvatar?: string }>>>({});
  const typingTimeoutsRef = useRef<Record<string, any>>({});
  const myTypingTimerRef = useRef<any>(null);
  const isMyTypingRef = useRef(false);

  // Estados de Gestión de Grupo
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [selectedNewMembers, setSelectedNewMembers] = useState<string[]>([]);
  const [uploadingGroupAvatar, setUploadingGroupAvatar] = useState(false);

  const handleUpdateGroupName = async () => {
    if (!editingGroupName.trim() || !active) return;
    try {
      await api.patch(`/chat/conversations/${active.id}`, { name: editingGroupName.trim() });
      setActive((prev: any) => ({ ...prev, name: editingGroupName.trim(), displayName: editingGroupName.trim() }));
      toast.success('Nombre del grupo actualizado');
      loadConvs();
    } catch {
      toast.error('Error al actualizar nombre');
    }
  };

  const handleGroupAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    setUploadingGroupAvatar(true);
    try {
      const newAvatarUrl = await upload(file);
      if (newAvatarUrl) {
        await api.patch(`/chat/conversations/${active.id}`, { avatar: newAvatarUrl });
        setActive((prev: any) => ({ ...prev, groupAvatar: newAvatarUrl, avatar: newAvatarUrl }));
        setConvs((prev: any[]) => prev.map(c => c.id === active.id ? { ...c, groupAvatar: newAvatarUrl, avatar: newAvatarUrl } : c));
        toast.success('Foto del grupo actualizada');
        loadConvs();
      }
    } catch {
      toast.error('Error al subir foto del grupo');
    } finally {
      setUploadingGroupAvatar(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleAddMembersToGroup = async () => {
    if (!active || selectedNewMembers.length === 0) return;
    try {
      const res = await api.post(`/chat/conversations/${active.id}/members`, { userIds: selectedNewMembers });
      setActive((prev: any) => ({ ...prev, members: res.data.members }));
      setSelectedNewMembers([]);
      toast.success('Miembros agregados con éxito');
      loadConvs();
    } catch {
      toast.error('Error al agregar miembros');
    }
  };

  const handleRemoveMemberFromGroup = async (userId: string) => {
    if (!active) return;
    if (!window.confirm('¿Deseas remover este integrante del grupo?')) return;
    try {
      await api.delete(`/chat/conversations/${active.id}/members/${userId}`);
      setActive((prev: any) => ({ ...prev, members: prev.members?.filter((m: any) => (m.userId || m.user?.id) !== userId) }));
      toast.success('Miembro removido');
      loadConvs();
    } catch {
      toast.error('Error al remover miembro');
    }
  };


  const [editingMsg, setEditingMsg] = useState<any>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<any>(null);

  // Iniciar edición de mensaje (Abre modal Glassmorphism)
  const startEditMessage = (m: any) => {
    if (!isMessageEditable(m.createdAt)) {
      toast.error('Solo puedes editar mensajes dentro de las primeras 3 horas posteriores al envío.');
      return;
    }
    setEditingMsg(m);
    setEditText(m.content || '');
  };

  // Guardar edición de mensaje y actualizar la misma burbuja de chat
  const handleSaveEditMessage = async () => {
    if (!editingMsg || !editText.trim() || savingEdit) return;
    if (!isMessageEditable(editingMsg.createdAt)) {
      toast.error('El tiempo límite para editar este mensaje (3 horas) ha expirado.');
      setEditingMsg(null);
      setEditText('');
      return;
    }
    setSavingEdit(true);
    try {
      const res = await api.put(`/chat/messages/${editingMsg.id}`, { content: editText.trim() });
      const updatedData = res.data;
      setMsgs(prev => prev.map(m => m.id === editingMsg.id ? { ...m, ...updatedData, content: editText.trim(), isEdited: true } : m));
      setEditingMsg(null);
      setEditText('');
      toast.success('Mensaje editado');
    } catch (err: any) {
      console.error('Error al editar mensaje:', err);
      const errMsg = err?.response?.data?.error || 'Error al editar mensaje';
      toast.error(errMsg);
    } finally {
      setSavingEdit(false);
    }
  };

  // Eliminar mensaje con confirmación
  const handleDeleteMessage = async (msgId: string) => {
    if (!window.confirm('¿Deseas eliminar este mensaje?')) return;
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMsgs(prev => prev.filter(m => m.id !== msgId));
      toast.success('Mensaje eliminado');
    } catch {
      toast.error('Error al eliminar mensaje');
    }
  };

  // Votar en encuestas interactivas desde la versión Web
  const handleVotePoll = async (messageId: string, optionId: string) => {
    if (!myId) return;

    // Actualización optimista inmediata
    setMsgs(prev =>
      prev.map(msg => {
        if (msg.id !== messageId) return msg;
        try {
          const poll = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
          const allowMultiple = !!poll.allowMultiple;
          poll.options = (poll.options || []).map((opt: any) => {
            let votes: string[] = Array.isArray(opt.votes) ? [...opt.votes] : [];
            if (opt.id === optionId) {
              if (votes.includes(myId)) {
                votes = votes.filter((uid: string) => uid !== myId);
              } else {
                votes.push(myId);
              }
            } else if (!allowMultiple) {
              votes = votes.filter((uid: string) => uid !== myId);
            }
            return { ...opt, votes };
          });
          return { ...msg, content: JSON.stringify(poll) };
        } catch {
          return msg;
        }
      })
    );

    try {
      const res = await api.post(`/chat/messages/${messageId}/poll-vote`, { optionId });
      if (res.data) {
        setMsgs(prev => prev.map(msg => (msg.id === messageId ? { ...msg, ...res.data } : msg)));
      }
    } catch (err) {
      console.error('Error voting on poll:', err);
      toast.error('No se pudo registrar el voto');
    }
  };

  // Eliminar conversación completa
  const handleDeleteConversation = async (convId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta conversación completa? Se borrará todo el historial.')) return;
    try {
      await api.delete(`/chat/conversations/${convId}`);
      setActive(null);
      loadConvs();
      toast.success('Conversación eliminada');
    } catch {
      toast.error('Error al eliminar conversación');
    }
  };

  const [uploading, setUploading] = useState(false);
  const [uploadingSticker, setUploadingSticker] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [gallery, setGallery] = useState<{ items: Array<{ url: string; type: string; name?: string }>; index: number } | null>(null);

  const openGallery = (items: Array<{ url: string; type: string; name?: string }>, index: number = 0) => {
    setGallery({ items, index });
  };

  // Modal Nuevo Chat y Modal Grupo
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [searchMember, setSearchMember] = useState('');
  const [showGrp, setShowGrp] = useState(false);
  const [grpName, setGrpName] = useState('');
  const [selM, setSelM] = useState<string[]>([]);

  const socketRef = useRef<Socket | null>(null);

  // Referencias UI
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const forceScrollRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stickerInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<any>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunks = useRef<BlobPart[]>([]);
  const recTimer = useRef<any>(null);
  const recStreamRef = useRef<MediaStream | null>(null);
  const recIsCanceledRef = useRef<boolean>(false);
  const activeRef = useRef<any>(null);
  const lastTs = useRef(0);
  const skipAutoScrollBottomRef = useRef(false);

  // Sincronización en vivo para Socket y Eventos de Modo Concentración
  const focusModeRef = useRef(focusMode);
  focusModeRef.current = focusMode;
  const focusAllowedRef = useRef(focusAllowedUserIds);
  focusAllowedRef.current = focusAllowedUserIds;
  const mutedDuringFocusRef = useRef<{ [senderId: string]: { count: number, name: string } }>({});

  const handleToggleFocusMode = (nextState?: boolean) => {
    const willEnable = typeof nextState === 'boolean' ? nextState : !focusMode;
    if (willEnable) {
      setFocusMode(true);
      focusModeRef.current = true;
      localStorage.setItem('zoik_focus_mode_enabled', 'true');
      mutedDuringFocusRef.current = {};

      // Si el chat actualmente abierto es un directo que no fue elegido, cambiar al primer chat permitido
      if (activeRef.current && activeRef.current.type !== 'GROUP') {
        const curOtherId = activeRef.current.otherUser?.id || activeRef.current.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.userId;
        if (!curOtherId || !focusAllowedUserIds.includes(curOtherId)) {
          const firstAllowed = convs.find(c => {
            if (c.type === 'GROUP') return true;
            const uId = c.otherUser?.id || c.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.userId;
            return uId && focusAllowedUserIds.includes(uId);
          });
          if (firstAllowed) {
            setActive(firstAllowed);
          }
        }
      }

      toast.success('🧘 Modo Concentración activado. Solo se muestran los chats elegidos y grupos.', {
        icon: '🧘',
        duration: 4000
      });
    } else {
      setFocusMode(false);
      focusModeRef.current = false;
      localStorage.setItem('zoik_focus_mode_enabled', 'false');

      // Al salir del modo concentración: notificar si hubo mensajes de personas silenciadas
      const mutedEntries = Object.entries(mutedDuringFocusRef.current);
      if (mutedEntries.length > 0) {
        playPing();
        const totalMutedMsgs = mutedEntries.reduce((sum, [, v]) => sum + v.count, 0);
        const sendersList = mutedEntries.map(([, v]) => v.name).filter(Boolean).join(', ');

        toast(`🔔 Has salido del Modo Concentración.\nRecibiste ${totalMutedMsgs} mensaje${totalMutedMsgs > 1 ? 's' : ''} de: ${sendersList}`, {
          icon: '📬',
          duration: 7000,
          style: {
            background: 'linear-gradient(135deg, #0f2a4a 0%, #1e4976 100%)',
            color: '#ffffff',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.2)'
          }
        });
      } else {
        toast('Modo Concentración desactivado. Notificaciones normales restauradas.', { icon: '🔔' });
      }
      mutedDuringFocusRef.current = {};
    }
  };

  const handleToggleAllowedUser = (userId: string) => {
    setFocusAllowedUserIds(prev => {
      const next = prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId];
      focusAllowedRef.current = next;
      localStorage.setItem('zoik_focus_allowed_users', JSON.stringify(next));
      return next;
    });
  };

  // Cambio de Estado Personal
  const handleStatusChange = (newStatus: string) => {
    setChatStatus(newStatus);
    localStorage.setItem('chatStatus', newStatus);
    toast.success(`Estado actualizado a: ${USER_STATUSES.find(s => s.id === newStatus)?.label}`);
  };

  // Inicializar Socket Estable
  useEffect(() => {
    if (!myId) return;

    const socket = io('https://zoikanalyst.com', { path: '/socket.io', transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('user:join', myId);
    });

    socket.on('chat:message', (newMsg: any) => {
      if (!newMsg || !newMsg.conversationId) return;

      const isCurrentActive = activeRef.current?.id === newMsg.conversationId;
      const isMe = newMsg.senderId === myId;
      const isFocusActive = focusModeRef.current;

      // Determinar si el mensaje pertenece a un grupo
      const targetConv = convs.find(c => c.id === newMsg.conversationId) || (isCurrentActive ? activeRef.current : null);
      const isGroup = targetConv ? targetConv.type === 'GROUP' : false;

      // Un mensaje está permitido si: no hay modo concentración, o es un grupo, o es enviado por mí, o el remitente está permitido
      const isAllowed = !isFocusActive || isGroup || isMe || focusAllowedRef.current.includes(newMsg.senderId);

      // Si está en Modo Concentración y NO está permitido (mensaje por interno de alguien no seleccionado)
      if (isFocusActive && !isAllowed && !isMe) {
        // Registrar silenciosamente para el reporte al salir del modo concentración
        const senderName = newMsg.sender ? `${newMsg.sender.firstName || ''} ${newMsg.sender.lastName || ''}`.trim() : 'Un compañero';
        if (!mutedDuringFocusRef.current[newMsg.senderId]) {
          mutedDuringFocusRef.current[newMsg.senderId] = { count: 1, name: senderName };
        } else {
          mutedDuringFocusRef.current[newMsg.senderId].count += 1;
        }

        // Actualizar la lista lateral de conversaciones sin alertar ni sonar
        setConvs(prev => prev.map(c => {
          if (c.id === newMsg.conversationId) {
            return {
              ...c,
              lastMessage: newMsg,
              unread: isCurrentActive ? 0 : (c.unread || 0) + 1,
              isMutedByFocus: true
            };
          }
          return c;
        }));

        // Si casualmente el chat está abierto en pantalla, agregar el mensaje al historial
        if (isCurrentActive) {
          setMsgs(prev => {
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          setTotalMsgCount(prev => prev + 1);
        }

        // NO sonar playPing() ni alertas
        return;
      }

      // Actualizar la lista lateral de conversaciones en tiempo real (flujo normal / permitido / grupos)
      setConvs(prev => prev.map(c => {
        if (c.id === newMsg.conversationId) {
          return {
            ...c,
            lastMessage: newMsg,
            unread: (isCurrentActive || isMe) ? 0 : (c.unread || 0) + 1,
            isMutedByFocus: false
          };
        }
        return c;
      }));

      // Si el mensaje corresponde al chat abierto actualmente
      if (isCurrentActive) {
        setMsgs(prev => {
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        setTotalMsgCount(prev => prev + 1);

        // Remover indicador de typing del remitente
        if (newMsg.senderId) {
          setTypingMap(prev => {
            if (!prev[newMsg.conversationId] || !prev[newMsg.conversationId][newMsg.senderId]) return prev;
            const updated = { ...prev[newMsg.conversationId] };
            delete updated[newMsg.senderId];
            return { ...prev, [newMsg.conversationId]: updated };
          });
        }

        if (!isMe) {
          playPing();
        }

        const container = scrollContainerRef.current;
        if (container) {
          const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
          if (distFromBottom < 180 || isMe) {
            forceScrollRef.current = true;
          }
        }
      } else {
        if (!isMe) {
          playPing();
        }
      }
    });

    socket.on('chat:group-updated', (updatedConv: any) => {
      const gAvatar = updatedConv.groupAvatar || updatedConv.avatar || null;
      setConvs((prev: any[]) => prev.map(c => c.id === updatedConv.id ? { ...c, ...updatedConv, groupAvatar: gAvatar, avatar: gAvatar } : c));
      setActive((prev: any) => prev?.id === updatedConv.id ? { ...prev, ...updatedConv, groupAvatar: gAvatar, avatar: gAvatar } : prev);
      loadConvs();
    });

    socket.on('chat:message-edited', (updated: any) => {
      setMsgs(prev => prev.map(m => m.id === updated.id ? updated : m));
    });

    socket.on('chat:message-deleted', ({ messageId }: any) => {
      setMsgs(prev => prev.filter(m => m.id !== messageId));
    });

    socket.on('chat:conversation-deleted', ({ conversationId }: any) => {
      loadConvs();
      setActive((prev: any) => prev?.id === conversationId ? null : prev);
    });

    socket.on('chat:typing', (data: { conversationId: string; fromUserId: string; isTyping: boolean; userName?: string; userAvatar?: string }) => {
      if (!data || !data.conversationId || data.fromUserId === myId) return;
      const { conversationId, fromUserId, isTyping, userName, userAvatar } = data;
      const timeoutKey = `${conversationId}_${fromUserId}`;

      if (isTyping) {
        setTypingMap(prev => ({
          ...prev,
          [conversationId]: {
            ...(prev[conversationId] || {}),
            [fromUserId]: {
              userName: userName || 'Alguien',
              userAvatar: userAvatar || ''
            }
          }
        }));

        // Safety fallback de 10 minutos por si la persona cierra el navegador abruptamente
        if (typingTimeoutsRef.current[timeoutKey]) {
          clearTimeout(typingTimeoutsRef.current[timeoutKey]);
        }
        typingTimeoutsRef.current[timeoutKey] = setTimeout(() => {
          setTypingMap(prev => {
            const convUsers = { ...(prev[conversationId] || {}) };
            delete convUsers[fromUserId];
            return { ...prev, [conversationId]: convUsers };
          });
        }, 10 * 60 * 1000);
      } else {
        if (typingTimeoutsRef.current[timeoutKey]) {
          clearTimeout(typingTimeoutsRef.current[timeoutKey]);
          delete typingTimeoutsRef.current[timeoutKey];
        }
        setTypingMap(prev => {
          const convUsers = { ...(prev[conversationId] || {}) };
          delete convUsers[fromUserId];
          return { ...prev, [conversationId]: convUsers };
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [myId]);

  const handleStartCall = (isVideo: boolean) => {
    if (!active) return;
    let otherUser = active.otherUser;
    if (!otherUser && active.members && Array.isArray(active.members)) {
      const found = active.members.find((m: any) => {
        const uId = m.user?.id || m.userId;
        return uId && uId !== myId;
      });
      otherUser = found?.user || found;
    }
    const targetId = otherUser?.id || otherUser?.userId || otherUser?._id;
    if (!otherUser || !targetId) {
      toast.error('Las llamadas directas aplican en chats 1 a 1');
      return;
    }
    triggerGlobalCall({ ...otherUser, id: targetId }, isVideo);
  };

  // Carga de Datos
  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get('/chat/conversations');
      const data = Array.isArray(r.data) ? r.data : [];
      setConvs(data);

      const cur = activeRef.current;
      if (cur) {
        const upd = data.find((c: any) => c.id === cur.id);
        if (upd) {
          const isGroup = upd.type === 'GROUP';
          const ou = isGroup ? null : (upd.otherUser || upd.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.user);
          setActive((prev: any) => ({
            ...prev,
            ...upd,
            groupAvatar: isGroup ? (upd.groupAvatar || upd.avatar || prev.groupAvatar || null) : null,
            avatar: isGroup ? (upd.groupAvatar || upd.avatar || prev.groupAvatar || null) : (upd.avatar || ou?.avatar || null),
            members: upd.members,
            otherUser: ou || null
          }));
        }
      } else if (data.length > 0) {
        setActive(data[0]);
      }
    } catch {}
  }, [myId]);

  const loadMsgs = useCallback(async (id: string, forceScroll = false, isInitial = false) => {
    try {
      const r = await api.get(`/chat/conversations/${id}/messages`);
      const { messages, hasMore, totalCount } = r.data;
      if (lastTs.current > 0 && myId && Array.isArray(messages)) {
        const nov = messages.filter((m: any) => m.senderId !== myId && new Date(m.createdAt).getTime() > lastTs.current);
        if (nov.length > 0) {
          // Si está en modo concentración, verificar si es grupo o si el remitente está permitido
          const isGroupChat = activeRef.current?.type === 'GROUP';
          const isAllowed = !focusModeRef.current || isGroupChat || nov.some((m: any) => focusAllowedRef.current.includes(m.senderId));
          if (isAllowed) {
            playPing();
          }
          // Cuando llega un mensaje nuevo de alguien, quitar inmediatamente su estado de typing
          nov.forEach((m: any) => {
            setTypingMap(prev => {
              if (!prev[id] || !prev[id][m.senderId]) return prev;
              const updated = { ...prev[id] };
              delete updated[m.senderId];
              return { ...prev, [id]: updated };
            });
          });
        }
      }
      const mx = (messages || []).reduce((a: number, m: any) => Math.max(a, new Date(m.createdAt).getTime()), 0);
      if (mx > 0) lastTs.current = mx;

      if (isInitial) {
        setMsgs(messages || []);
        setHasMoreOlder(!!hasMore);
        if (typeof totalCount === 'number') setTotalMsgCount(totalCount);
      } else {
        // En polling o updates suaves: NUNCA sobreescribir ni borrar mensajes anteriores ya cargados por el usuario
        setMsgs(prev => {
          if (!messages || messages.length === 0) return prev;
          const map = new Map<string, any>();
          for (const m of prev) map.set(m.id, m);
          for (const m of messages) map.set(m.id, m);
          return Array.from(map.values()).sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
        if (typeof totalCount === 'number') setTotalMsgCount(totalCount);
      }

      if (forceScroll) forceScrollRef.current = true;
    } catch {}
  }, [myId]);

  const loadOlderMsgs = useCallback(async () => {
    if (!active?.id || loadingOlder || !hasMoreOlder || msgs.length === 0) return;
    const oldest = msgs[0];
    if (!oldest?.id) return;

    setLoadingOlder(true);
    const container = scrollContainerRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    try {
      const r = await api.get(`/chat/conversations/${active.id}/messages`, {
        params: { cursor: oldest.id, limit: 50 }
      });
      const olderList: any[] = r.data.messages || [];
      const hasMore: boolean = !!r.data.hasMore;

      if (olderList.length > 0) {
        skipAutoScrollBottomRef.current = true;
        setMsgs(prev => {
          const map = new Map<string, any>();
          for (const m of olderList) map.set(m.id, m);
          for (const m of prev) map.set(m.id, m);
          return Array.from(map.values()).sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });

        // Mantener la posición de scroll exacta para que no brinque la pantalla
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
          }
        });
      }

      setHasMoreOlder(hasMore);
      if (typeof r.data.totalCount === 'number') {
        setTotalMsgCount(r.data.totalCount);
      }
    } catch (err) {
      console.error('Error al cargar historial anterior:', err);
    } finally {
      setLoadingOlder(false);
    }
  }, [active?.id, loadingOlder, hasMoreOlder, msgs]);

  useEffect(() => {
    const openId = (location.state as any)?.openConvId;
    if (openId && convs.length > 0) {
      const found = convs.find((c: any) => c.id === openId);
      if (found) {
        setActive(found);
        lastTs.current = 0;
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [(location.state as any)?.openConvId, convs]);

  useEffect(() => {
    const targetUserId = (location.state as any)?.targetUserId;
    if (targetUserId) {
      api.post('/chat/conversations/direct', { targetUserId }).then(r => {
        loadConvs();
        const oth = r.data.otherUser || r.data.members?.find((m: any) => (m.userId || m.user?.id) === targetUserId)?.user;
        setActive({ ...r.data, otherUser: oth, displayName: oth ? `${oth.firstName} ${oth.lastName}` : (r.data.displayName || 'Chat'), type: 'DIRECT' });
        navigate(location.pathname, { replace: true, state: {} });
      }).catch(() => {});
    }
  }, [(location.state as any)?.targetUserId]);

  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    loadConvs();
    api.get('/chat/team').then(r => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/chat/stickers').then(r => setStickers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [loadConvs]);

  const sendTypingStatus = useCallback((isTyping: boolean) => {
    if (!socketRef.current || !activeRef.current?.id || !myId) return;
    const currentActive = activeRef.current;
    let toUserId: string | undefined = undefined;
    if (currentActive.type !== 'GROUP') {
      const other = currentActive.members?.find((m: any) => (m.userId || m.user?.id) !== myId);
      toUserId = other?.userId || other?.user?.id || currentActive.otherUser?.id;
    }
    const myName = `${authUser?.firstName || user.firstName || ''} ${authUser?.lastName || user.lastName || ''}`.trim() || 'Usuario';
    const myAvatar = (authUser as any)?.avatar || user.avatar || '';

    socketRef.current.emit('chat:typing', {
      conversationId: currentActive.id,
      fromUserId: myId,
      toUserId,
      isTyping,
      userName: myName,
      userAvatar: myAvatar
    });
    isMyTypingRef.current = isTyping;
  }, [myId, authUser, user.firstName, user.lastName, user.avatar]);

  useEffect(() => {
    if (!active) return;
    lastTs.current = 0;
    setShowScrollBottom(false);
    setHasMoreOlder(false);
    loadMsgs(active.id, true, true);

    if (socketRef.current) {
      socketRef.current.emit('chat:join-conversation', active.id);
    }

    pollRef.current = setInterval(() => {
      loadMsgs(active.id, false, false);
      loadConvs();
    }, 3000);

    return () => {
      clearInterval(pollRef.current);
      if (socketRef.current && active?.id) {
        socketRef.current.emit('chat:leave-conversation', active.id);
      }
      if (myTypingTimerRef.current) {
        clearTimeout(myTypingTimerRef.current);
        myTypingTimerRef.current = null;
      }
      if (isMyTypingRef.current) {
        sendTypingStatus(false);
      }
    };
  }, [active?.id, loadMsgs, loadConvs, sendTypingStatus]);

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (skipAutoScrollBottomRef.current) {
      skipAutoScrollBottomRef.current = false;
      return;
    }
    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      el.scrollTop = el.scrollHeight;
      return;
    }
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, [msgs, typingMap]);

  const handleChatScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBottom(distFromBottom > 160);

    // Si el usuario scrollea hacia arriba al inicio, cargar automáticamente mensajes más viejos
    if (el.scrollTop < 80 && hasMoreOlder && !loadingOlder) {
      loadOlderMsgs();
    }
  }, [hasMoreOlder, loadingOlder, loadOlderMsgs]);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });
    setShowScrollBottom(false);
  }, []);

  const scrollToMessage = useCallback((msgId: string) => {
    if (!msgId) return;
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'all 0.3s ease';
      el.style.filter = 'brightness(1.25) drop-shadow(0 0 12px rgba(0, 112, 243, 0.4))';
      setTimeout(() => {
        el.style.filter = 'none';
      }, 1400);
    }
  }, []);

  const upload = async (file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await api.post('/uploads', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (evt) => {
        if (evt.total && onProgress) {
          const pct = Math.round((evt.loaded * 100) / evt.total);
          onProgress(pct);
        }
      }
    });
    return r.data.url;
  };

  const sendMsg = async (opts: { content?: string, fileUrl?: string, fileType?: string, fileName?: string } = {}) => {
    if (!active || sending) return;
    if (!opts.fileUrl && !opts.content && uploading) return;
    let { content = input.trim(), fileUrl, fileType, fileName } = opts;

    // Caso 1: Archivo individual directo (e.g. Stickers, notas de voz)
    if (fileUrl) {
      setIsFlying(true);
      setTimeout(() => setIsFlying(false), 750);
      if (myTypingTimerRef.current) {
        clearTimeout(myTypingTimerRef.current);
        myTypingTimerRef.current = null;
      }
      sendTypingStatus(false);
      setSending(true);
      try {
        await api.post(`/chat/conversations/${active.id}/messages`, {
          content,
          fileUrl,
          fileType,
          fileName,
          mentions: mentionedUserIds,
          replyToId: replyTo?.id || null
        });
        setInput('');
        setMentionedUserIds([]);
        setReplyTo(null);
        setShowPicker(false);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        forceScrollRef.current = true;
        await loadMsgs(active.id, true);
      } catch {
        toast.error('Error al enviar');
      } finally {
        setSending(false);
      }
      return;
    }

    // Caso 2: Archivos adjuntos agrupados en un solo mensaje estilo WhatsApp
    if (attachments.length > 0) {
      setUploading(true);
      setSending(true);
      setUploadProgress(0);
      setIsFlying(true);
      setTimeout(() => setIsFlying(false), 750);
      if (myTypingTimerRef.current) {
        clearTimeout(myTypingTimerRef.current);
        myTypingTimerRef.current = null;
      }
      sendTypingStatus(false);

      try {
        if (attachments.length === 1) {
          const single = attachments[0];
          const url = await upload(single.file, (pct) => setUploadProgress(pct));
          await api.post(`/chat/conversations/${active.id}/messages`, {
            content: content || '',
            fileUrl: url,
            fileType: single.type,
            fileName: single.name,
            mentions: mentionedUserIds,
            replyToId: replyTo?.id || null
          });
        } else {
          // Subir todos los archivos adjuntos con barra de progreso ponderada por tamaño
          const totalBytes = attachments.reduce((sum, a) => sum + (a.file?.size || 0), 0) || 1;
          const loadedBytesMap: Record<number, number> = {};

          const uploadedList = await Promise.all(
            attachments.map(async (att, idx) => {
              const url = await upload(att.file, (pct) => {
                loadedBytesMap[idx] = ((att.file?.size || 0) * pct) / 100;
                const totalLoaded = Object.values(loadedBytesMap).reduce((s, b) => s + b, 0);
                setUploadProgress(Math.min(99, Math.round((totalLoaded * 100) / totalBytes)));
              });
              return {
                url,
                type: att.type,
                name: att.name,
                size: att.size,
                ext: att.ext
              };
            })
          );

          await api.post(`/chat/conversations/${active.id}/messages`, {
            content: content || '',
            fileUrl: JSON.stringify(uploadedList),
            fileType: 'media_group',
            fileName: `${uploadedList.length} archivos`,
            mentions: mentionedUserIds,
            replyToId: replyTo?.id || null
          });
        }

        setInput('');
        setAttachments([]);
        setUploadProgress(null);
        setMentionedUserIds([]);
        setReplyTo(null);
        setShowPicker(false);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        forceScrollRef.current = true;
        await loadMsgs(active.id, true);
        toast.success(attachments.length > 1 ? `¡Álbum de ${attachments.length} archivos enviado!` : 'Archivo enviado');
      } catch (err) {
        console.error('Error al enviar adjuntos:', err);
        toast.error('Error al subir y enviar archivos');
      } finally {
        setUploading(false);
        setSending(false);
        setUploadProgress(null);
      }
      return;
    }

    // Caso 3: Mensaje de texto simple sin adjuntos
    if (!content) return;

    setIsFlying(true);
    setTimeout(() => setIsFlying(false), 750);
    if (myTypingTimerRef.current) {
      clearTimeout(myTypingTimerRef.current);
      myTypingTimerRef.current = null;
    }
    sendTypingStatus(false);

    setSending(true);
    try {
      await api.post(`/chat/conversations/${active.id}/messages`, {
        content,
        mentions: mentionedUserIds,
        replyToId: replyTo?.id || null
      });
      setInput('');
      setMentionedUserIds([]);
      setReplyTo(null);
      setShowPicker(false);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      forceScrollRef.current = true;
      await loadMsgs(active.id, true);
    } catch {
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  // Subir Nuevo Sticker
  const handleUploadSticker = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSticker(true);
    try {
      const url = await upload(file);
      await api.post('/chat/stickers', { url, name: file.name });
      const r = await api.get('/chat/stickers');
      setStickers(Array.isArray(r.data) ? r.data : []);
      toast.success('✓ ¡Sticker agregado a tu colección!');
    } catch {
      toast.error('Error al subir sticker');
    } finally {
      setUploadingSticker(false);
      if (e.target) e.target.value = '';
    }
  };

  // Guardar Sticker en Favoritos desde el Chat
  const handleToggleReaction = async (msgId: string, emoji: string) => {
    try {
      const res = await api.post(`/chat/messages/${msgId}/react`, { emoji });
      setMsgs(prev => prev.map(m => m.id === msgId ? { ...m, reactions: res.data.reactions } : m));
    } catch {
      toast.error('Error al reaccionar');
    }
    setActiveReactionMsgId(null);
  };

  const handleSaveSticker = async (url: string) => {
    try {
      await api.post('/chat/stickers', { url, name: 'Sticker Favorito' });
      const r = await api.get('/chat/stickers');
      setStickers(Array.isArray(r.data) ? r.data : []);
      toast.success('⭐ ¡Sticker guardado en favoritos!');
    } catch {
      toast.error('Error al guardar sticker');
    }
  };

  const openDirect = async (u: any) => {
    try {
      const r = await api.post('/chat/conversations/direct', { targetUserId: u.id });
      setActive({ ...r.data, displayName: `${u.firstName} ${u.lastName}`, type: 'DIRECT' });
      await loadConvs();
    } catch {
      toast.error('Error al abrir chat');
    }
  };

  const createGroup = async () => {
    if (!grpName.trim() || selM.length === 0) {
      toast.error('Indica un nombre y al menos 1 miembro');
      return;
    }
    try {
      const r = await api.post('/chat/conversations/group', { name: grpName, memberIds: selM });
      setActive({ ...r.data, displayName: grpName, type: 'GROUP' });
      setShowGrp(false);
      setGrpName('');
      setSelM([]);
      await loadConvs();
      toast.success('Grupo creado con éxito');
    } catch {
      toast.error('Error al crear grupo');
    }
  };

  const getSupportedAudioMime = () => {
    const candidateTypes = [
      { mime: 'audio/mp4', ext: 'm4a' },
      { mime: 'audio/aac', ext: 'aac' },
      { mime: 'audio/webm;codecs=opus', ext: 'webm' },
      { mime: 'audio/webm', ext: 'webm' },
      { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
    ];
    if (typeof MediaRecorder === 'undefined') return { mime: '', ext: 'm4a' };
    for (const item of candidateTypes) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(item.mime)) {
        return item;
      }
    }
    return { mime: '', ext: 'm4a' };
  };

  const startRec = async () => {
    try {
      recIsCanceledRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      recStreamRef.current = stream;

      const { mime: selectedMime, ext } = getSupportedAudioMime();
      const options: MediaRecorderOptions = selectedMime ? { mimeType: selectedMime } : {};
      const rec = new MediaRecorder(stream, options);

      recChunks.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recChunks.current.push(e.data);
        }
      };

      rec.onstop = async () => {
        if (recStreamRef.current) {
          recStreamRef.current.getTracks().forEach((t) => t.stop());
          recStreamRef.current = null;
        }
        if (recIsCanceledRef.current) {
          recChunks.current = [];
          return;
        }
        if (!recChunks.current.length) {
          toast.error('No se grabó audio');
          return;
        }

        const mimeType = selectedMime || 'audio/webm';
        const blob = new Blob(recChunks.current, { type: mimeType });
        if (blob.size < 100) {
          toast.error('Nota de voz demasiado corta');
          return;
        }

        const fileName = `nota-${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: mimeType });

        setUploading(true);
        try {
          const url = await upload(file);
          setUploading(false);
          await sendMsg({ content: '', fileUrl: url, fileType: 'audio', fileName: 'Nota de voz' });
        } catch (err) {
          console.error('Error al subir nota de voz:', err);
          toast.error('Error al enviar audio');
        } finally {
          setUploading(false);
          recChunks.current = [];
        }
      };

      rec.start(250);
      recRef.current = rec;
      setIsRec(true);
      setRecT(0);
      if (recTimer.current) clearInterval(recTimer.current);
      recTimer.current = setInterval(() => setRecT((t) => t + 1), 1000);
    } catch (err) {
      console.error('Error al acceder al micrófono:', err);
      toast.error('🔒 Haz clic en el candado 🔒 junto a la URL y permite el micrófono', { duration: 6000 });
    }
  };

  const stopRec = () => {
    recIsCanceledRef.current = false;
    if (recTimer.current) {
      clearInterval(recTimer.current);
      recTimer.current = null;
    }
    setIsRec(false);
    setRecT(0);
    if (recRef.current && recRef.current.state !== 'inactive') {
      recRef.current.stop();
    }
    recRef.current = null;
  };

  const cancelRec = () => {
    recIsCanceledRef.current = true;
    if (recTimer.current) {
      clearInterval(recTimer.current);
      recTimer.current = null;
    }
    setIsRec(false);
    setRecT(0);
    if (recRef.current && recRef.current.state !== 'inactive') {
      recRef.current.stop();
    }
    recRef.current = null;
    if (recStreamRef.current) {
      recStreamRef.current.getTracks().forEach((t) => t.stop());
      recStreamRef.current = null;
    }
    recChunks.current = [];
    toast('Grabación cancelada', { icon: '🗑️' });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFilesToAttachments(pastedFiles);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInput(val);

    // Auto-ajustar altura
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;

    // Mantener estado de Typing activo mientras haya texto en el input
    if (val.trim().length > 0) {
      if (!isMyTypingRef.current) {
        sendTypingStatus(true);
      }
    } else {
      if (isMyTypingRef.current) {
        sendTypingStatus(false);
      }
    }

    // Detectar si el usuario está escribiendo una mención con @
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/(?:^|\s)@([a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ]*)$/);

    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (targetUser: any) => {
    const cursorPos = textareaRef.current?.selectionStart || input.length;
    const textBeforeCursor = input.slice(0, cursorPos);
    const textAfterCursor = input.slice(cursorPos);
    const atIdx = textBeforeCursor.lastIndexOf('@');

    if (atIdx !== -1) {
      const fullName = `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim() || 'Usuario';
      const newText = textBeforeCursor.slice(0, atIdx) + `@${fullName} ` + textAfterCursor;
      setInput(newText);
      setMentionedUserIds(prev => Array.from(new Set([...prev, targetUser.id])));
      setMentionQuery(null);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newCursor = atIdx + fullName.length + 2;
          textareaRef.current.setSelectionRange(newCursor, newCursor);
        }
      }, 50);
    }
  };

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const source = (active?.type === 'GROUP' && active?.members && active.members.length > 0)
      ? active.members.map((m: any) => m.user || team.find((t: any) => t.id === (m.userId || m.id)) || { id: m.userId, firstName: 'Usuario', lastName: '' }).filter((u: any) => u && u.id)
      : team;

    const seen = new Set();
    const uniqueSource: any[] = [];
    for (const u of source) {
      if (u.id && u.id !== myId && !seen.has(u.id)) {
        seen.add(u.id);
        uniqueSource.push(u);
      }
    }

    if (!mentionQuery.trim()) {
      return uniqueSource.slice(0, 8);
    }

    const q = mentionQuery.toLowerCase();
    return uniqueSource.filter((u: any) => {
      const full = `${u.firstName || ''} ${u.lastName || ''} ${u.role || ''} ${u.email || ''}`.toLowerCase();
      return full.includes(q);
    }).slice(0, 8);
  }, [mentionQuery, active, team, myId]);

  const addFilesToAttachments = (fileList: FileList | File[]) => {
    const newItems: AttachmentItem[] = Array.from(fileList).map(file => {
      const meta = getFileMeta(file.name, file.type);
      return {
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        url: URL.createObjectURL(file),
        type: meta.type,
        name: file.name,
        size: file.size,
        ext: (file.name.split('.').pop() || '').toLowerCase()
      };
    });
    setAttachments(prev => [...prev, ...newItems]);
    if (newItems.length > 0) {
      toast.success(`${newItems.length} archivo${newItems.length > 1 ? 's' : ''} preparado${newItems.length > 1 ? 's' : ''}`);
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const clearAllAttachments = () => {
    setAttachments([]);
  };

  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFilesToAttachments(e.target.files);
    }
    if (e.target) e.target.value = '';
  };

  const handleFile = handleFilesSelect;

  const filteredConvs = convs.filter(c => {
    // En Modo Concentración, SOLO se muestran en el sidebar los grupos y los chats directos que el usuario eligió. No se deben ver más.
    if (focusMode) {
      if (c.type !== 'GROUP') {
        const otherUserId = c.otherUser?.id || c.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.userId;
        if (!otherUserId || !focusAllowedUserIds.includes(otherUserId)) {
          return false; // Totalmente oculto del sidebar
        }
      }
    }

    if (filter === 'direct' && c.type !== 'DIRECT') return false;
    if (filter === 'groups' && c.type !== 'GROUP') return false;
    if (search.trim()) {
      const name = (c.displayName || c.name || '').toLowerCase();
      return name.includes(search.toLowerCase());
    }
    return true;
  });

  const matchingTeam = search.trim()
    ? team.filter(u => {
        if (u.id === myId) return false;
        // En Modo Concentración, no mostrar personas que no fueron elegidas
        if (focusMode && !focusAllowedUserIds.includes(u.id)) return false;
        const fullName = `${u.firstName} ${u.lastName} ${u.role || ''} ${u.email || ''}`.toLowerCase();
        return fullName.includes(search.toLowerCase());
      })
    : [];

  const currentStatusObj = USER_STATUSES.find(s => s.id === chatStatus) || USER_STATUSES[0];

  const activeTypingUsers = active?.id && typingMap[active.id]
    ? Object.entries(typingMap[active.id]).map(([userId, info]) => ({ userId, ...info }))
    : [];

  return (
    <div className="zoik-page-container" style={{ display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden', background: '#f4f7f6', fontFamily: '"Montserrat", sans-serif' }}>
      <Sidebar />

      {/* ESTILOS Y KEYFRAMES DE ANIMACIONES DE ESTADO Y TYPING */}
      <style>{`
        @media (max-width: 767px) {
          .chat-list-col {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            border-right: none !important;
            height: calc(100vh - 56px - 60px - var(--sat) - var(--sab)) !important;
            height: calc(100dvh - 56px - 60px - var(--sat) - var(--sab)) !important;
          }
          .chat-list-col.mobile-hidden {
            display: none !important;
          }
          .chat-main-col {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            height: calc(100vh - 56px - 60px - var(--sat) - var(--sab)) !important;
            height: calc(100dvh - 56px - 60px - var(--sat) - var(--sab)) !important;
          }
          .chat-main-col.mobile-hidden {
            display: none !important;
          }
          .chat-header-pad {
            padding: 8px 12px !important;
          }
          .chat-input-pad {
            padding: 6px 8px calc(8px + var(--sab)) !important;
          }
          .chat-input-bubble {
            padding: 4px 6px !important;
            gap: 2px !important;
            border-radius: 24px !important;
          }
          .chat-action-btn {
            padding: 5px !important;
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
          }
          .chat-send-btn {
            width: 34px !important;
            height: 34px !important;
            min-width: 34px !important;
          }
        }
        @keyframes zoikGearSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes zoikZzzFloat1 {
          0% { transform: translateY(2px) scale(0.85); opacity: 0.2; }
          40% { transform: translateY(-1px) scale(1); opacity: 1; }
          80% { transform: translateY(-4px) scale(1.05); opacity: 0.15; }
          100% { transform: translateY(-5px) scale(1.05); opacity: 0; }
        }
        @keyframes zoikZzzFloat2 {
          0% { transform: translateY(2px) scale(0.8); opacity: 0.15; }
          40% { transform: translateY(-2px) scale(1); opacity: 0.95; }
          80% { transform: translateY(-6px) scale(1.1); opacity: 0.15; }
          100% { transform: translateY(-7px) scale(1.1); opacity: 0; }
        }
        @keyframes zoikZzzFloat3 {
          0% { transform: translateY(1px) scale(0.75); opacity: 0.1; }
          40% { transform: translateY(-3px) scale(1); opacity: 0.85; }
          80% { transform: translateY(-8px) scale(1.2); opacity: 0.1; }
          100% { transform: translateY(-9px) scale(1.2); opacity: 0; }
        }
        @keyframes zoikPacmanChompTopOnly {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(-36deg); }
        }
        @keyframes zoikPacmanChompBottomOnly {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(36deg); }
        }
        @keyframes zoikPacmanCookieMove {
          0% { transform: translate(12px, -50%); opacity: 1; }
          65% { opacity: 1; }
          95%, 100% { transform: translate(-2px, -50%); opacity: 0; }
        }
        @keyframes zoikPlugDisconnect {
          0%, 15% { transform: translateX(0px); }
          40%, 75% { transform: translateX(5.5px); }
          90%, 100% { transform: translateX(0px); }
        }
        @keyframes zoikSocketPull {
          0%, 15% { transform: translateX(0px); }
          40%, 75% { transform: translateX(-3px); }
          90%, 100% { transform: translateX(0px); }
        }
        @keyframes zoikSparkPulse {
          0%, 20% { opacity: 0; transform: scale(0.3); }
          35% { opacity: 1; transform: scale(1.4); }
          55%, 100% { opacity: 0; transform: scale(0.3); }
        }
        @keyframes zoikTypingBounce {
          0%, 80%, 100% {
            transform: translateY(0);
            opacity: 0.45;
          }
          40% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }
        @keyframes zoikTypingDot1 {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes zoikTypingDot2 {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          55% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes zoikTypingDot3 {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          70% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes zoikPlaneTakeoff {
          0% {
            transform: translate(0, 0) scale(1) rotate(0deg);
            opacity: 1;
          }
          18% {
            transform: translate(-3px, 3px) scale(0.92) rotate(-8deg);
            opacity: 1;
          }
          48% {
            transform: translate(36px, -36px) scale(0.5) rotate(20deg);
            opacity: 0;
          }
          52% {
            transform: translate(-32px, 32px) scale(0.5) rotate(0deg);
            opacity: 0;
          }
          78% {
            transform: translate(2px, -2px) scale(1.06) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate(0, 0) scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        @keyframes zoikDrawerSlideUp {
          0% {
            opacity: 0;
            transform: translateY(12px) scale(0.99);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .EmojiPickerReact {
          border: none !important;
          font-family: "Montserrat", sans-serif !important;
          background: transparent !important;
          --epr-bg-color: transparent !important;
          --epr-category-label-bg-color: rgba(248, 250, 252, 0.95) !important;
        }
        .EmojiPickerReact .epr-search-container input.epr-search {
          border-radius: 12px !important;
          font-family: "Montserrat", sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          border: 1.5px solid #e2e8f0 !important;
          background: #f8fafc !important;
        }
        .EmojiPickerReact .epr-search-container input.epr-search:focus {
          border-color: #0070f3 !important;
          box-shadow: 0 0 0 3px rgba(0, 112, 243, 0.1) !important;
        }
        @keyframes zoikFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoikModalPop {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes zoikTabUnfoldMe {
          0% {
            opacity: 0;
            transform: scaleX(0.1) scaleY(0.7) translateX(35px);
            transform-origin: right center;
          }
          65% {
            opacity: 1;
            transform: scaleX(1.04) scaleY(1.02) translateX(-4px);
            transform-origin: right center;
          }
          85% {
            transform: scaleX(0.99) scaleY(0.99) translateX(1px);
            transform-origin: right center;
          }
          100% {
            opacity: 1;
            transform: scaleX(1) scaleY(1) translateX(0);
            transform-origin: right center;
          }
        }
        @keyframes zoikTabUnfoldOther {
          0% {
            opacity: 0;
            transform: scaleX(0.1) scaleY(0.7) translateX(-35px);
            transform-origin: left center;
          }
          65% {
            opacity: 1;
            transform: scaleX(1.04) scaleY(1.02) translateX(4px);
            transform-origin: left center;
          }
          85% {
            transform: scaleX(0.99) scaleY(0.99) translateX(-1px);
            transform-origin: left center;
          }
          100% {
            opacity: 1;
            transform: scaleX(1) scaleY(1) translateX(0);
            transform-origin: left center;
          }
        }
        @keyframes zoikWaveMove1 {
          0% {
            transform: translateX(0) scaleY(1);
          }
          50% {
            transform: translateX(-22%) scaleY(1.12) translateY(-12px);
          }
          100% {
            transform: translateX(-45%) scaleY(0.96) translateY(8px);
          }
        }
        @keyframes zoikWaveMove2 {
          0% {
            transform: translateX(-35%) scaleY(0.96) translateY(8px);
          }
          50% {
            transform: translateX(-15%) scaleY(1.15) translateY(-15px);
          }
          100% {
            transform: translateX(0%) scaleY(1) translateY(0px);
          }
        }
        @keyframes zoikRecordPulse {
          0% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            opacity: 0.85;
            transform: scale(1.15);
            box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
          }
          100% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
        @keyframes zoikSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {/* CONTENEDOR 3 COLUMNAS */}
      <div className="zoik-chat-main" style={{ flex: 1, display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>

        {/* COLUMNA 1: LISTA DE CHATS (COLAPSABLE / DESPLEGABLE CON GLASSMORPHISM) */}
        <div className={`chat-list-col ${active ? 'mobile-hidden' : ''}`} style={{
          width: isChatListCollapsed ? '80px' : '330px',
          minWidth: isChatListCollapsed ? '80px' : '300px',
          height: '100%',
          background: 'linear-gradient(180deg, #e4effc 0%, #ebf4fd 100%)',
          borderRight: '1.5px solid rgba(191, 219, 254, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          position: 'relative',
          transition: 'all 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
          zIndex: 20
        }}>
          {/* PESTAÑA / TIRADOR LATERAL DISCRETO (TAB HANDLE) */}
          <button
            onClick={() => setIsChatListCollapsed(!isChatListCollapsed)}
            title={isChatListCollapsed ? "Desplegar panel de chats" : "Plegar panel de chats"}
            style={{
              position: 'absolute',
              right: '-16px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '46px',
              borderRadius: '0 8px 8px 0',
              background: '#0070f3',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              borderLeft: 'none',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 50,
              padding: 0,
              transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.width = '20px';
              e.currentTarget.style.right = '-20px';
              e.currentTarget.style.boxShadow = '2px 0 10px rgba(0, 112, 243, 0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.width = '16px';
              e.currentTarget.style.right = '-16px';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: isChatListCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s ease'
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* CABECERA DE SELECCIÓN DE CHATS Y OPCIONES */}
          {!isChatListCollapsed ? (
            <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid rgba(191, 219, 254, 0.6)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#0f2a4a', letterSpacing: '-0.5px' }}>Chats</h2>
                  
                  {/* SELECTOR DE ESTADO PERSONAL CON ANIMACIONES EN VIVO */}
                  <div style={{ position: 'relative', marginTop: '6px' }}>
                    <button
                      onClick={() => setShowStatusMenu(!showStatusMenu)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '7px',
                        padding: '4px 9px',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.82)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${currentStatusObj.color}45`,
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(15, 42, 74, 0.05)'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.98)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.82)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <StatusIndicator status={chatStatus} size={15} />
                      <span style={{ fontSize: '11px', fontWeight: 800, color: currentStatusObj.color, fontFamily: '"Montserrat", sans-serif' }}>
                        {currentStatusObj.label}
                      </span>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={currentStatusObj.color} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showStatusMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {/* MENU FLOTANTE GLASSMORPHISM DE ESTADOS */}
                    {showStatusMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          zIndex: 100,
                          minWidth: '175px',
                          padding: '6px',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(240, 246, 255, 0.93) 100%)',
                          backdropFilter: 'blur(16px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                          border: '1px solid rgba(255, 255, 255, 0.95)',
                          boxShadow: '0 12px 32px rgba(15, 42, 74, 0.18), 0 2px 6px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px'
                        }}
                      >
                        {USER_STATUSES.map(s => {
                          const isSelected = s.id === chatStatus;
                          return (
                            <button
                              key={s.id}
                              onClick={() => {
                                handleStatusChange(s.id);
                                setShowStatusMenu(false);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 10px',
                                borderRadius: '10px',
                                border: isSelected ? `1px solid ${s.color}35` : '1px solid transparent',
                                background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                                cursor: 'pointer',
                                outline: 'none',
                                transition: 'all 0.15s ease',
                                textAlign: 'left',
                                width: '100%'
                              }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{ width: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <StatusIndicator status={s.id} size={16} />
                              </div>
                              <span style={{ fontSize: '12px', fontWeight: isSelected ? 800 : 700, color: isSelected ? s.color : '#0f2a4a', fontFamily: '"Montserrat", sans-serif', flex: 1 }}>
                                {s.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Botón Modo Concentración */}
                  <button
                    onClick={() => setShowFocusModal(true)}
                    title={focusMode ? `Modo Concentración ACTIVO (${focusAllowedUserIds.length} permitidos + Grupos)` : 'Activar Modo Concentración en el Trabajo'}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      border: focusMode ? '1.5px solid rgba(255, 255, 255, 0.4)' : '1.5px solid rgba(79, 70, 229, 0.25)',
                      background: focusMode
                        ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                        : '#ffffff',
                      color: focusMode ? '#ffffff' : '#4f46e5',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: focusMode ? '0 4px 14px rgba(79, 70, 229, 0.35)' : 'none',
                      transition: 'all 0.18s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="6" />
                      <circle cx="12" cy="12" r="2" />
                    </svg>
                    {focusMode && (
                      <span style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        width: '9px',
                        height: '9px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        border: '2px solid #ffffff'
                      }} />
                    )}
                  </button>

                  {/* Botón Nuevo Chat Individual */}
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    title="Nuevo Chat Individual"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#0070f3',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                    onMouseDown={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.96)'; }}
                    onMouseUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <line x1="12" y1="8" x2="12" y2="14" />
                      <line x1="9" y1="11" x2="15" y2="11" />
                    </svg>
                  </button>

                  {/* Botón Nuevo Grupo */}
                  <button
                    onClick={() => setShowGrp(true)}
                    title="Crear Nuevo Grupo"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#0070f3',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                    onMouseDown={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.96)'; }}
                    onMouseUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* BANNER MODO CONCENTRACIÓN ACTIVO */}
              {focusMode && (
                <div style={{
                  marginBottom: '12px',
                  padding: '10px 14px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 16px rgba(79, 70, 229, 0.25)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>
                      🧘
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '-0.2px' }}>Modo Concentración</span>
                      <span style={{ fontSize: '10px', opacity: 0.9, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {focusAllowedUserIds.length} contacto{focusAllowedUserIds.length === 1 ? '' : 's'} + Grupos
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      onClick={() => setShowFocusModal(true)}
                      title="Editar personas permitidas"
                      style={{
                        padding: '4px 9px',
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Ajustar
                    </button>
                    <button
                      onClick={() => handleToggleFocusMode(false)}
                      title="Desactivar modo concentración"
                      style={{
                        padding: '4px 9px',
                        borderRadius: '8px',
                        background: '#ffffff',
                        border: 'none',
                        color: '#4f46e5',
                        fontSize: '11px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      Salir
                    </button>
                  </div>
                </div>
              )}

              {/* FILTROS DE CHAT EN GLASSMORPHISM PILLS */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
                {[
                  { id: 'all', label: focusMode ? 'Todos (Enfocados)' : 'Todos' },
                  { id: 'direct', label: focusMode ? 'Directos (Elegidos)' : 'Directos' },
                  { id: 'groups', label: 'Grupos & Marcas' }
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setFilter(t.id as any)}
                    style={{
                      padding: '5px 11px',
                      borderRadius: '20px',
                      border: filter === t.id ? '1px solid rgba(15, 42, 74, 0.8)' : '1px solid rgba(255, 255, 255, 0.8)',
                      background: filter === t.id ? (t.id === 'focus' ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)' : 'linear-gradient(135deg, #0f2a4a 0%, #173d6b 100%)') : 'rgba(255, 255, 255, 0.65)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      color: filter === t.id ? '#ffffff' : '#0f2a4a',
                      fontSize: '11px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: filter === t.id ? '0 4px 12px rgba(15, 42, 74, 0.18)' : '0 2px 6px rgba(15, 42, 74, 0.03)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* BUSCADOR CON LUPA EN TRAZO LIMPIO */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.95)',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 2px 8px rgba(15, 42, 74, 0.04), inset 0 1px 2px rgba(255,255,255,0.8)'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0070f3" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar conversación..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#0f2a4a',
                    width: '100%',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                />
              </div>
            </div>
          ) : (
            /* CABECERA CUANDO ESTÁ COLAPSADO */
            <div style={{ padding: '20px 8px 14px', borderBottom: '1px solid rgba(191, 219, 254, 0.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              {/* SELECTOR DESPLEGABLE ÚNICO DE FILTRO */}
              <select
                value={filter}
                onChange={e => setFilter(e.target.value as any)}
                title="Filtrar por tipo"
                style={{
                  width: '54px',
                  padding: '6px 4px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(8px)',
                  color: '#0f2a4a',
                  fontSize: '9px',
                  fontWeight: 900,
                  cursor: 'pointer',
                  outline: 'none',
                  textAlign: 'center',
                  fontFamily: '"Montserrat", sans-serif',
                  boxShadow: '0 2px 6px rgba(15, 42, 74, 0.05)'
                }}
              >
                <option value="all">Todos</option>
                <option value="direct">Directos</option>
                <option value="groups">Grupos</option>
              </select>

              {/* BOTÓN DE ESTADO ANIMADO (COLAPSADO) */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  title={`Estado actual: ${currentStatusObj.label}`}
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: `1.5px solid ${currentStatusObj.color}60`,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(15, 42, 74, 0.05)',
                    transition: 'all 0.2s ease',
                    padding: 0
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <StatusIndicator status={chatStatus} size={17} />
                </button>

                {showStatusMenu && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: 'calc(100% + 10px)',
                      zIndex: 100,
                      minWidth: '175px',
                      padding: '6px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(240, 246, 255, 0.93) 100%)',
                      backdropFilter: 'blur(16px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                      border: '1px solid rgba(255, 255, 255, 0.95)',
                      boxShadow: '0 12px 32px rgba(15, 42, 74, 0.2), 0 2px 6px rgba(0,0,0,0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px'
                    }}
                  >
                    {USER_STATUSES.map(s => {
                      const isSelected = s.id === chatStatus;
                      return (
                        <button
                          key={s.id}
                          onClick={() => {
                            handleStatusChange(s.id);
                            setShowStatusMenu(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 10px',
                            borderRadius: '10px',
                            border: isSelected ? `1px solid ${s.color}35` : '1px solid transparent',
                            background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'all 0.15s ease',
                            textAlign: 'left',
                            width: '100%'
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ width: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <StatusIndicator status={s.id} size={16} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: isSelected ? 800 : 700, color: isSelected ? s.color : '#0f2a4a', fontFamily: '"Montserrat", sans-serif', flex: 1 }}>
                            {s.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                {/* Botón Nuevo Chat */}
                <button
                  onClick={() => setShowNewChatModal(true)}
                  title="Nuevo Chat Individual"
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#0070f3',
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <line x1="12" y1="8" x2="12" y2="14" />
                    <line x1="9" y1="11" x2="15" y2="11" />
                  </svg>
                </button>

                {/* Botón Nuevo Grupo */}
                <button
                  onClick={() => setShowGrp(true)}
                  title="Crear Nuevo Grupo"
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#0070f3',
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* LISTA DE CHATS */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: isChatListCollapsed ? '12px 6px' : '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: isChatListCollapsed ? '12px' : '10px',
            alignItems: isChatListCollapsed ? 'center' : 'stretch'
          }}>
            {filteredConvs.length === 0 && (!search.trim() || matchingTeam.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '40px 10px', color: 'rgba(15,42,74,0.4)', fontSize: '12px', fontWeight: 600 }}>
                {!isChatListCollapsed
                  ? (focusMode
                      ? 'Solo los chats elegidos y grupos se muestran en Modo Concentración. Usa "Ajustar" para elegir compañeros.'
                      : 'No hay conversaciones activas.')
                  : '—'}
              </div>
            ) : (
              <>
                {filteredConvs.map(c => {
                  const isActive = active?.id === c.id;
                  const isGroup = c.type === 'GROUP';
                  const otherUser = isGroup ? null : (c.otherUser || c.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.user);
                  const name = isGroup ? (c.name || c.displayName || 'Grupo') : (c.displayName || (otherUser ? `${otherUser.firstName} ${otherUser.lastName}` : 'Chat'));
                  const avatar = isGroup ? (c.groupAvatar || c.avatar || null) : (c.avatar || otherUser?.avatar || null);
                  const lastMsg = c.lastMessage;
                  const isConvTyping = Boolean(typingMap[c.id] && Object.keys(typingMap[c.id]).length > 0);
                  const isLoc = lastMsg?.fileType === 'location' || (typeof lastMsg?.content === 'string' && lastMsg.content.includes('"latitude"') && lastMsg.content.includes('"longitude"'));
                  const isPl = lastMsg?.fileType === 'poll' || (typeof lastMsg?.content === 'string' && lastMsg.content.includes('"question"') && lastMsg.content.includes('"options"'));
                  const isEv = lastMsg?.fileType === 'event' || (typeof lastMsg?.content === 'string' && lastMsg.content.includes('"title"') && (lastMsg.content.includes('"meetingType"') || lastMsg.content.includes('"date"')));
                  const isCt = lastMsg?.fileType === 'contact' || (typeof lastMsg?.content === 'string' && lastMsg.content.includes('"name"') && (lastMsg.content.includes('"phone"') || lastMsg.content.includes('"phoneNumber"')));
                  const previewText = lastMsg?.fileType === 'audio' ? '🎤 Nota de voz' :
                                      lastMsg?.fileType === 'sticker' ? '🎭 Sticker' :
                                      lastMsg?.fileType === 'image' ? '📷 Imagen' :
                                      lastMsg?.fileType === 'video' ? '🎥 Video' :
                                      isLoc ? '📍 Ubicación' :
                                      isPl ? '📊 Encuesta' :
                                      isEv ? '📅 Evento' :
                                      isCt ? '👤 Contacto' :
                                      lastMsg?.content || 'Sin mensajes aún';

                  if (isChatListCollapsed) {
                    {/* MODO COLAPSADO: SOLO CÍRCULOS DE IMÁGENES */}
                    return (
                      <div
                        key={c.id}
                        onClick={() => { setActive(c); setConvs(prev => prev.map(conv => conv.id === c.id ? { ...conv, unread: 0 } : conv)); window.dispatchEvent(new CustomEvent('chat:read')); }}
                        title={`${name}${isConvTyping ? ' (Escribiendo...)' : ''}`}
                        style={{
                          position: 'relative',
                          width: '46px',
                          height: '46px',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          padding: '2px',
                          background: isActive ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                          boxShadow: 'none',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          background: colorFor(name),
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 900,
                          overflow: 'hidden'
                        }}>
                          {avatar ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(name)}
                        </div>
                        {isConvTyping && (
                          <span style={{
                            position: 'absolute',
                            bottom: '-2px',
                            right: '-2px',
                            background: '#0070f3',
                            borderRadius: '50%',
                            width: '14px',
                            height: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #ffffff',
                            boxShadow: 'none'
                          }}>
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#ffffff', animation: 'zoikTypingDot1 1s infinite ease-in-out' }} />
                          </span>
                        )}
                        {c.unread > 0 && !isConvTyping && (
                          <span style={{
                            position: 'absolute',
                            top: '-2px',
                            right: '-2px',
                            background: '#0070f3',
                            color: 'white',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 900,
                            border: '2px solid #ffffff',
                            boxShadow: 'none'
                          }}>
                            {c.unread}
                          </span>
                        )}
                      </div>
                    );
                  }

                  {/* MODO DESPLEGADO: TARJETAS EN GLASSMORPHISM TRASLÚCIDO */}
                  return (
                    <div
                      key={c.id}
                      onClick={() => { setActive(c); setConvs(prev => prev.map(conv => conv.id === c.id ? { ...conv, unread: 0 } : conv)); window.dispatchEvent(new CustomEvent('chat:read')); }}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '16px',
                        background: isActive
                          ? 'linear-gradient(135deg, #0f2a4a 0%, #173d6b 100%)'
                          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.55) 100%)',
                        backdropFilter: 'blur(14px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                        border: isActive ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(255, 255, 255, 0.88)',
                        boxShadow: isActive
                          ? '0 8px 24px rgba(15, 42, 74, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.3)'
                          : '0 4px 14px rgba(15, 42, 74, 0.04), inset 0 1px 2px rgba(255, 255, 255, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
                      }}
                      onMouseEnter={e => {
                        if (!isActive) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderColor = 'rgba(0, 112, 243, 0.4)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 42, 74, 0.08)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.75) 100%)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isActive) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.88)';
                          e.currentTarget.style.boxShadow = '0 4px 14px rgba(15, 42, 74, 0.04), inset 0 1px 2px rgba(255, 255, 255, 0.95)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.55) 100%)';
                        }
                      }}
                    >
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        background: colorFor(name),
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 900,
                        flexShrink: 0,
                        overflow: 'hidden',
                        border: isActive ? '2px solid rgba(255,255,255,0.4)' : '2px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 2px 8px rgba(15, 42, 74, 0.12)'
                      }}>
                        {avatar ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 800, color: isActive ? '#ffffff' : '#0f2a4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                          {lastMsg && <span style={{ fontSize: '10px', color: isActive ? 'rgba(255,255,255,0.65)' : 'rgba(15,42,74,0.45)', fontWeight: 700 }}>{fmtT(lastMsg.createdAt)}</span>}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {isConvTyping ? (
                            <span style={{ fontSize: '11px', color: isActive ? '#60a5fa' : '#0070f3', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
                                <span style={{ width: '3.5px', height: '3.5px', borderRadius: '50%', background: isActive ? '#93c5fd' : '#0070f3', animation: 'zoikTypingDot1 1.2s infinite ease-in-out' }} />
                                <span style={{ width: '3.5px', height: '3.5px', borderRadius: '50%', background: isActive ? '#93c5fd' : '#0070f3', animation: 'zoikTypingDot2 1.2s infinite ease-in-out' }} />
                                <span style={{ width: '3.5px', height: '3.5px', borderRadius: '50%', background: isActive ? '#93c5fd' : '#0070f3', animation: 'zoikTypingDot3 1.2s infinite ease-in-out' }} />
                              </span>
                              escribiendo...
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', color: isActive ? 'rgba(255,255,255,0.8)' : 'rgba(15,42,74,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '170px', fontWeight: 500 }}>
                              {previewText}
                            </span>
                          )}
                          {c.unread > 0 && <span style={{ background: '#0070f3', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 900, boxShadow: 'none' }}>{c.unread}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* SI ESTÁ BUSCANDO, MOSTRAR INTEGRANTES DEL EQUIPO PARA INICIAR CHAT DIRECTO */}
                {search.trim() && matchingTeam.length > 0 && (
                  <div style={{ marginTop: '10px', borderTop: '1px solid rgba(191, 219, 254, 0.6)', paddingTop: '10px' }}>
                    <div style={{ padding: '0 8px 8px', fontSize: '10.5px', fontWeight: 800, color: '#0070f3', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <IconPlus size={13} color="#0070f3" /> Iniciar Chat con Equipo
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {matchingTeam.map(t => {
                        const memberName = `${t.firstName} ${t.lastName}`;
                        return (
                          <div
                            key={t.id}
                            onClick={() => {
                              openDirect(t);
                              setSearch('');
                            }}
                            style={{
                              padding: '10px 12px',
                              borderRadius: '14px',
                              background: '#ffffff',
                              border: '1px solid rgba(0, 112, 243, 0.2)',
                              boxShadow: '0 2px 8px rgba(0, 112, 243, 0.06)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#0070f3'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0, 112, 243, 0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: colorFor(memberName), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, overflow: 'hidden', flexShrink: 0 }}>
                                {t.avatar ? <img src={t.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(memberName)}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f2a4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {memberName}
                                </span>
                                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
                                  {t.role || 'Equipo'} • {t.email}
                                </span>
                              </div>
                            </div>
                            <span style={{ fontSize: '10.5px', fontWeight: 800, color: '#ffffff', background: '#0070f3', padding: '4px 8px', borderRadius: '8px', flexShrink: 0 }}>
                              + Mensaje
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* COLUMNA 2: CONVERSACIÓN */}
        <div className={`chat-main-col ${!active ? 'mobile-hidden' : ''}`} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', background: '#eef5fd', position: 'relative', overflow: 'hidden' }}>
          {/* FONDO PATRÓN DOODLE ILUSTRADO OFICIAL (SUTIL Y ELEGANTE) */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/assets/chat-wallpaper.jpg)',
            backgroundRepeat: 'repeat',
            backgroundSize: '460px auto',
            backgroundPosition: 'center',
            opacity: 0.18,
            pointerEvents: 'none',
            zIndex: 0
          }} />

          {active ? (
            <>
              {/* Cabecera */}
              <div className="chat-header-pad" style={{ padding: '16px 28px', borderBottom: '1.5px solid rgba(191, 219, 254, 0.6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', flexShrink: 0, position: 'relative', zIndex: 2 }}>
                {(() => {
                  const isGroup = active.type === 'GROUP';
                  const headerName = isGroup ? (active.name || active.displayName || 'Grupo') : (active.displayName || active.name || (active.otherUser ? `${active.otherUser.firstName} ${active.otherUser.lastName}` : 'Chat'));
                  const headerAvatar = isGroup ? (active.groupAvatar || active.avatar || null) : (active.avatar || active.otherUser?.avatar || null);

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {/* Botón Volver en Móvil */}
                      <button
                        className="zoik-mobile-only"
                        onClick={() => setActive(null)}
                        style={{
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: '10px',
                          padding: '6px 10px',
                          fontSize: '13px',
                          fontWeight: 900,
                          color: '#0070f3',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0
                        }}
                      >
                        ←
                      </button>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: colorFor(headerName), color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 900, overflow: 'hidden', flexShrink: 0 }}>
                        {headerAvatar ? <img src={headerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(headerName)}
                      </div>
                      <div
                        onClick={() => {
                          if (active.type === 'GROUP') {
                            setEditingGroupName(active.displayName || active.name || '');
                            setShowGroupModal(true);
                          }
                        }}
                        style={{ cursor: active.type === 'GROUP' ? 'pointer' : 'default' }}
                        title={active.type === 'GROUP' ? 'Ver información y miembros del grupo' : ''}
                      >
                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {headerName}
                        </div>
                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {activeTypingUsers.length > 0 ? (
                        <span style={{ color: '#0070f3', display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 800 }}>
                          <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#0070f3', animation: 'zoikTypingDot1 1.2s infinite ease-in-out' }} />
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#0070f3', animation: 'zoikTypingDot2 1.2s infinite ease-in-out' }} />
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#0070f3', animation: 'zoikTypingDot3 1.2s infinite ease-in-out' }} />
                          </span>
                          {activeTypingUsers.length === 1
                            ? (active.type === 'GROUP' ? `${activeTypingUsers[0].userName} está escribiendo...` : 'escribiendo...')
                            : 'Varios están escribiendo...'}
                        </span>
                      ) : active.type === 'GROUP' ? (
                        <>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }} />
                          {active.members?.length || 0} miembros (Gestionar)
                        </>
                      ) : (
                        <>
                          <StatusIndicator status="disponible" size={13} />
                          En línea
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {active.type !== 'GROUP' ? (
                    <>
                      <button
                        onClick={() => handleStartCall(false)}
                        title="Llamada de Voz"
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '10px',
                          border: '1.5px solid #cbd5e1',
                          background: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0f2a4a',
                          padding: 0
                        }}
                      >
                        <IconPhone size={18} color="#0f2a4a" />
                      </button>
                      <button
                        onClick={() => handleStartCall(true)}
                        title="Videollamada en Vivo"
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '10px',
                          border: '1.5px solid #cbd5e1',
                          background: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0f2a4a',
                          padding: 0
                        }}
                      >
                        <IconVideo size={18} color="#0f2a4a" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingGroupName(active.displayName || active.name || '');
                        setShowGroupModal(true);
                      }}
                      title="Opciones y Miembros del Grupo"
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        border: '1.5px solid #cbd5e1',
                        background: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0070f3',
                        padding: 0
                      }}
                    >
                      <IconSettings size={18} color="#0070f3" />
                    </button>
                  )}

                  <button
                    onClick={() => handleDeleteConversation(active.id)}
                    title="Eliminar Conversación"
                    style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ef4444',
                      padding: 0
                    }}
                  >
                    <IconTrash size={18} color="#ef4444" />
                  </button>
                </div>
              </div>

              {/* Banner informativo de contacto silenciado por Modo Concentración */}
              {(() => {
                if (!focusMode || active.type === 'GROUP') return null;
                const other = active.otherUser || active.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.user;
                const otherId = other?.id || active.members?.find((m: any) => (m.user?.id || m.userId) !== myId)?.userId;
                if (!otherId || focusAllowedUserIds.includes(otherId)) return null;
                const otherName = other ? `${other.firstName} ${other.lastName}` : (active.displayName || 'Este usuario');

                return (
                  <div style={{
                    padding: '9px 24px',
                    background: 'linear-gradient(90deg, rgba(79, 70, 229, 0.12) 0%, rgba(99, 102, 241, 0.06) 100%)',
                    borderBottom: '1px solid rgba(79, 70, 229, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    fontSize: '12px',
                    color: '#4338ca',
                    fontWeight: 600,
                    zIndex: 2,
                    position: 'relative'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '15px' }}>🌙</span>
                      <span>
                        <strong>{otherName}</strong> está silenciado por el <strong>Modo Concentración</strong>. No recibirás alertas ni sonidos de sus mensajes.
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        handleToggleAllowedUser(otherId);
                        toast.success(`${otherName} añadido a personas permitidas`);
                      }}
                      style={{
                        padding: '4px 11px',
                        borderRadius: '8px',
                        background: '#4f46e5',
                        color: 'white',
                        border: 'none',
                        fontSize: '11px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(79, 70, 229, 0.25)'
                      }}
                    >
                      + Permitir contacto
                    </button>
                  </div>
                );
              })()}

              {/* Mensajes sobre fondo ondulante */}
              <div ref={scrollContainerRef} onScroll={handleChatScroll} style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '14px', background: 'transparent', position: 'relative', zIndex: 1 }}>
                {/* Botón o aviso de carga de mensajes anteriores */}
                {hasMoreOlder && (
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 12px' }}>
                    <button
                      onClick={loadOlderMsgs}
                      disabled={loadingOlder}
                      style={{
                        padding: '7px 18px',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1.5px solid rgba(15, 42, 74, 0.12)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
                        color: '#0f2a4a',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: loadingOlder ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {loadingOlder ? (
                        <>
                          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                          <span>Cargando mensajes anteriores...</span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '14px', lineHeight: 1 }}>↑</span>
                          <span>Cargar mensajes anteriores {totalMsgCount > msgs.length ? `(${msgs.length} de ${totalMsgCount})` : ''}</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
                {!hasMoreOlder && msgs.length > 0 && (
                  <div style={{ textAlign: 'center', margin: '4px 0 12px', opacity: 0.65, fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                    ✦ Inicio del historial {totalMsgCount > 0 ? `(${totalMsgCount} mensajes)` : ''} ✦
                  </div>
                )}

                {msgs.map((m, idx) => {
                  const isMe = m.senderId === myId;
                  const isSticker = m.fileType === 'sticker';
                  const showDate = idx === 0 || fmtD(m.createdAt) !== fmtD(msgs[idx - 1].createdAt);

                  return (
                    <React.Fragment key={m.id || idx}>
                      {showDate && (
                        <div style={{ textAlign: 'center', margin: '16px 0 8px' }}>
                          <span style={{
                            background: 'rgba(255, 255, 255, 0.85)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.95)',
                            color: '#0f2a4a',
                            fontSize: '10px',
                            fontWeight: 800,
                            padding: '4px 14px',
                            borderRadius: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            boxShadow: '0 2px 8px rgba(15, 42, 74, 0.05)'
                          }}>
                            {fmtD(m.createdAt)}
                          </span>
                        </div>
                      )}

                      <div
                        id={`msg-${m.id}`}
                        onMouseEnter={() => setHoveredMsgId(m.id)}
                        onMouseLeave={() => setHoveredMsgId(null)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', width: '100%', position: 'relative' }}
                      >
                        {/* RENDERIZADO DE STICKER */}
                        {isSticker ? (
                          <div
                            style={{
                              position: 'relative',
                              display: 'inline-block',
                              transformOrigin: isMe ? 'right center' : 'left center',
                              animation: isMe
                                ? 'zoikTabUnfoldMe 0.48s cubic-bezier(0.22, 1, 0.36, 1) both'
                                : 'zoikTabUnfoldOther 0.48s cubic-bezier(0.22, 1, 0.36, 1) both'
                            }}
                            className="sticker-bubble"
                          >
                            {/* Barra Flotante en Sticker */}
                            {hoveredMsgId === m.id && (
                              <div style={{
                                position: 'absolute',
                                top: '-12px',
                                right: isMe ? '0px' : 'auto',
                                left: isMe ? 'auto' : '0px',
                                background: 'rgba(255, 255, 255, 0.92)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.95)',
                                borderRadius: '20px',
                                boxShadow: '0 8px 24px rgba(15, 42, 74, 0.16), 0 2px 6px rgba(0,0,0,0.04)',
                                padding: '3px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                zIndex: 10
                              }}>
                                <button
                                  onClick={() => {
                                    setReplyTo(m);
                                    setTimeout(() => textareaRef.current?.focus(), 50);
                                  }}
                                  title="Responder"
                                  style={{ background: 'none', border: 'none', color: '#0070f3', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                                >
                                  <IconReply size={13} />
                                </button>
                                {!isMe && (
                                  <button onClick={() => handleSaveSticker(m.fileUrl)} title="Guardar en mis stickers" style={{ background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', padding: '1px' }}>
                                    ⭐
                                  </button>
                                )}
                                <button onClick={() => handleDeleteMessage(m.id)} title="Eliminar sticker" style={{ background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', color: '#ef4444', padding: '1px' }}>
                                  🗑️
                                </button>
                              </div>
                            )}
                            <img src={m.fileUrl} alt="Sticker" style={{ width: '130px', height: '130px', objectFit: 'contain', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.15))' }} />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMe ? 'flex-end' : 'flex-start', gap: '3px', fontSize: '9px', fontWeight: 700, color: 'rgba(15,42,74,0.4)', marginTop: '2px' }}>
                              <span>{fmtT(m.createdAt)}</span>
                              {isMe && <DoubleCheckIcon size={14} color="#0070f3" />}
                            </div>
                          </div>
                        ) : (() => {
                          const isAudioOnly = !m.content && (m.fileType === 'audio' || m.fileName === 'Nota de voz');
                          return (
                            /* BURBUJA DE MENSAJE EN GLASSMORPHISM PURO TRASLÚCIDO */
                            <div
                              style={{
                                position: 'relative',
                                maxWidth: isAudioOnly ? '340px' : '72%',
                                minWidth: isAudioOnly ? '260px' : 'auto',
                                padding: isAudioOnly ? '10px 14px 6px 14px' : '13px 18px',
                                borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                background: isMe
                                  ? 'linear-gradient(135deg, rgba(0, 112, 243, 0.92) 0%, rgba(0, 80, 204, 0.82) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.65) 100%)',
                                backdropFilter: 'blur(16px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                                color: isMe ? '#ffffff' : '#0f2a4a',
                                border: isMe ? '1px solid rgba(255, 255, 255, 0.35)' : '1px solid rgba(255, 255, 255, 0.95)',
                                boxShadow: isMe
                                  ? '0 8px 24px rgba(0, 112, 243, 0.22), inset 0 1px 1px rgba(255, 255, 255, 0.45), inset 0 -1px 2px rgba(0,0,0,0.1)'
                                  : '0 6px 20px rgba(15, 42, 74, 0.05), inset 0 1px 2px rgba(255, 255, 255, 0.95)',
                                fontSize: '13px',
                                lineHeight: 1.5,
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                transformOrigin: isMe ? 'right center' : 'left center',
                                animation: isMe
                                  ? 'zoikTabUnfoldMe 0.48s cubic-bezier(0.22, 1, 0.36, 1) both'
                                  : 'zoikTabUnfoldOther 0.48s cubic-bezier(0.22, 1, 0.36, 1) both'
                              }}
                            >
                            {/* Barra Flotante Universal en Glassmorphism */}
                            {hoveredMsgId === m.id && (
                              <div style={{
                                position: 'absolute',
                                top: '-14px',
                                right: isMe ? '10px' : 'auto',
                                left: isMe ? 'auto' : '10px',
                                background: 'rgba(255, 255, 255, 0.92)',
                                backdropFilter: 'blur(14px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                                border: '1px solid rgba(255, 255, 255, 0.95)',
                                borderRadius: '20px',
                                boxShadow: '0 8px 24px rgba(15, 42, 74, 0.16), 0 2px 6px rgba(0,0,0,0.04)',
                                padding: '3px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                zIndex: 10
                              }}>
                                {/* Reacciones Rápidas estilo WhatsApp */}
                                <div style={{ display: 'flex', gap: '5px', borderRight: '1px solid #e2e8f0', paddingRight: '6px', marginRight: '2px', alignItems: 'center' }}>
                                  {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(em => (
                                    <button
                                      key={em}
                                      onClick={() => handleToggleReaction(m.id, em)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.12s ease' }}
                                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.3)'}
                                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                      <WhatsAppEmoji emoji={em} size={20} />
                                    </button>
                                  ))}
                                </div>

                                <button
                                  onClick={() => {
                                    setReplyTo(m);
                                    setTimeout(() => textareaRef.current?.focus(), 50);
                                  }}
                                  title="Responder"
                                  style={{ background: 'none', border: 'none', color: '#0070f3', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                                >
                                  <IconReply size={13} />
                                </button>
                                {isMe && !isSticker && m.content && isMessageEditable(m.createdAt) && (
                                  <button
                                    onClick={() => startEditMessage(m)}
                                    title="Editar mensaje (disponible durante las primeras 3h)"
                                    style={{ background: 'none', border: 'none', color: '#0f2a4a', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                                  >
                                    <IconEdit size={13} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteMessage(m.id)}
                                  title="Eliminar mensaje / audio"
                                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                                >
                                  <IconTrash size={13} />
                                </button>
                              </div>
                            )}

                            {!isMe && active.type === 'GROUP' && m.sender && (
                              <div style={{ fontSize: '10px', fontWeight: 900, color: '#0070f3', marginBottom: '3px' }}>{m.sender.firstName} {m.sender.lastName}</div>
                            )}

                            {/* TARJETA DE MENSAJE CITADO / RESPUESTA (ESTILO WHATSAPP) */}
                            {m.replyTo && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scrollToMessage(m.replyTo.id);
                                }}
                                title="Ir al mensaje original"
                                style={{
                                  background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(0, 112, 243, 0.08)',
                                  borderLeft: isMe ? '3.5px solid #ffffff' : '3.5px solid #0070f3',
                                  borderRadius: '8px',
                                  padding: '5px 9px',
                                  marginBottom: '7px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '8px',
                                  backdropFilter: 'blur(4px)',
                                  WebkitBackdropFilter: 'blur(4px)'
                                }}
                              >
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 800, color: isMe ? '#ffffff' : '#0070f3', fontSize: '10.5px', marginBottom: '1px' }}>
                                    {m.replyTo.sender?.firstName || 'Usuario'} {m.replyTo.sender?.lastName || ''}
                                  </div>
                                  <div style={{ color: isMe ? 'rgba(255, 255, 255, 0.85)' : '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '11px' }}>
                                    {m.replyTo.fileType === 'image' ? '📷 Foto' :
                                     m.replyTo.fileType === 'video' ? '🎥 Video' :
                                     m.replyTo.fileType === 'audio' ? '🎤 Nota de voz' :
                                     m.replyTo.fileType === 'sticker' ? '🎭 Sticker' :
                                     m.replyTo.fileType === 'media_group' ? '📁 Archivos multimedia' :
                                     (m.replyTo.content || m.replyTo.fileName || 'Archivo adjunto')}
                                  </div>
                                </div>
                                {m.replyTo.fileUrl && (m.replyTo.fileType === 'image' || m.replyTo.fileType === 'sticker') && (
                                  <img
                                    src={m.replyTo.fileUrl}
                                    alt=""
                                    style={{ width: '30px', height: '30px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                                  />
                                )}
                              </div>
                            )}

                            {/* RENDERIZADO DE MENSAJES ESPECIALES: UBICACIÓN, ENCUESTAS, EVENTOS, CONTACTOS O TEXTO NORMAL */}
                            {(() => {
                              if (!m.content) return null;

                              const isLoc = m.fileType === 'location' || (typeof m.content === 'string' && m.content.startsWith('{') && m.content.includes('"latitude"') && m.content.includes('"longitude"'));
                              const isPl = m.fileType === 'poll' || (typeof m.content === 'string' && m.content.startsWith('{') && m.content.includes('"question"') && m.content.includes('"options"'));
                              const isEv = m.fileType === 'event' || (typeof m.content === 'string' && m.content.startsWith('{') && m.content.includes('"title"') && (m.content.includes('"meetingType"') || m.content.includes('"date"')));
                              const isCt = m.fileType === 'contact' || (typeof m.content === 'string' && m.content.startsWith('{') && m.content.includes('"name"') && (m.content.includes('"phone"') || m.content.includes('"phoneNumber"')));

                              // 1. TARJETA DE UBICACIÓN
                              if (isLoc) {
                                let locData: any = {};
                                try { locData = typeof m.content === 'string' ? JSON.parse(m.content) : m.content; } catch {}
                                const lat = locData.latitude;
                                const lng = locData.longitude;
                                const address = locData.address || 'Ubicación en tiempo real';
                                const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;

                                return (
                                  <div style={{
                                    width: '100%',
                                    minWidth: '260px',
                                    maxWidth: '340px',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(241, 245, 249, 0.95)',
                                    border: isMe ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(226, 232, 240, 0.9)',
                                    padding: '12px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                      <div style={{
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '10px',
                                        background: '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontSize: '18px',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.35)'
                                      }}>
                                        📍
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                          fontWeight: 700,
                                          fontSize: '13.5px',
                                          color: isMe ? '#ffffff' : '#0f172a',
                                          lineHeight: 1.3,
                                          wordBreak: 'break-word'
                                        }}>
                                          {address}
                                        </div>
                                        {lat != null && lng != null && (
                                          <div style={{
                                            fontSize: '11px',
                                            color: isMe ? 'rgba(255, 255, 255, 0.75)' : '#64748b',
                                            marginTop: '2px'
                                          }}>
                                            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <a
                                      href={mapUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        background: isMe ? '#ffffff' : '#0070f3',
                                        color: isMe ? '#0070f3' : '#ffffff',
                                        fontWeight: 700,
                                        fontSize: '12.5px',
                                        textDecoration: 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        transition: 'transform 0.15s ease'
                                      }}
                                      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                                      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                    >
                                      <span>🗺️</span>
                                      <span>Abrir en Google Maps</span>
                                    </a>
                                  </div>
                                );
                              }

                              // 2. TARJETA DE ENCUESTA INTERACTIVA
                              if (isPl) {
                                let pollData: any = {};
                                try { pollData = typeof m.content === 'string' ? JSON.parse(m.content) : m.content; } catch {}
                                const question = pollData.question || 'Encuesta';
                                const options: any[] = Array.isArray(pollData.options) ? pollData.options : [];
                                const allowMultiple = !!pollData.allowMultiple;
                                const totalVotes = options.reduce((sum, opt) => sum + (opt.votes?.length || 0), 0);

                                return (
                                  <div style={{
                                    width: '100%',
                                    minWidth: '270px',
                                    maxWidth: '340px',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(241, 245, 249, 0.95)',
                                    border: isMe ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(226, 232, 240, 0.9)',
                                    padding: '12px 14px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '16px' }}>📊</span>
                                      <span style={{
                                        fontWeight: 800,
                                        fontSize: '14.5px',
                                        color: isMe ? '#ffffff' : '#0f172a',
                                        lineHeight: 1.3
                                      }}>
                                        {question}
                                      </span>
                                    </div>
                                    <div style={{
                                      fontSize: '11px',
                                      color: isMe ? 'rgba(255, 255, 255, 0.75)' : '#64748b',
                                      marginTop: '-4px'
                                    }}>
                                      {allowMultiple ? 'Selecciona una o más opciones' : 'Selecciona una opción'}
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      {options.map((opt: any) => {
                                        const votesCount = opt.votes?.length || 0;
                                        const percentage = totalVotes > 0 ? Math.round((votesCount / totalVotes) * 100) : 0;
                                        const hasVoted = myId && opt.votes?.includes(myId);

                                        return (
                                          <div
                                            key={opt.id}
                                            onClick={() => handleVotePoll(m.id, opt.id)}
                                            style={{
                                              position: 'relative',
                                              borderRadius: '10px',
                                              border: hasVoted
                                                ? (isMe ? '1.5px solid #ffffff' : '1.5px solid #0070f3')
                                                : (isMe ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(203, 213, 225, 0.8)'),
                                              overflow: 'hidden',
                                              cursor: 'pointer',
                                              background: isMe
                                                ? (hasVoted ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)')
                                                : (hasVoted ? '#e0f2fe' : '#ffffff'),
                                              transition: 'all 0.15s ease',
                                              padding: '8px 10px'
                                            }}
                                          >
                                            {/* Barra de progreso */}
                                            <div
                                              style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                width: `${percentage}%`,
                                                background: isMe
                                                  ? (hasVoted ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)')
                                                  : (hasVoted ? 'rgba(0, 112, 243, 0.2)' : 'rgba(0, 0, 0, 0.05)'),
                                                transition: 'width 0.3s ease',
                                                zIndex: 1
                                              }}
                                            />

                                            <div style={{
                                              position: 'relative',
                                              zIndex: 2,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: '8px'
                                            }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                                                <span style={{ fontSize: '13px', lineHeight: 1 }}>
                                                  {hasVoted ? (allowMultiple ? '☑️' : '🔘') : (allowMultiple ? '⬜' : '⚪')}
                                                </span>
                                                <span style={{
                                                  fontSize: '13px',
                                                  fontWeight: hasVoted ? 700 : 500,
                                                  color: isMe ? '#ffffff' : '#0f172a',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis'
                                                }}>
                                                  {opt.text}
                                                </span>
                                              </div>
                                              <div style={{
                                                fontSize: '11.5px',
                                                fontWeight: 700,
                                                color: isMe ? '#ffffff' : '#0070f3',
                                                flexShrink: 0
                                              }}>
                                                {percentage}% ({votesCount})
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    <div style={{
                                      fontSize: '11px',
                                      color: isMe ? 'rgba(255, 255, 255, 0.75)' : '#64748b',
                                      textAlign: 'right',
                                      marginTop: '2px'
                                    }}>
                                      {totalVotes} {totalVotes === 1 ? 'voto' : 'votos'}
                                    </div>
                                  </div>
                                );
                              }

                              // 3. TARJETA DE EVENTO / REUNIÓN
                              if (isEv) {
                                let eventData: any = {};
                                try { eventData = typeof m.content === 'string' ? JSON.parse(m.content) : m.content; } catch {}
                                const title = eventData.title || 'Evento';
                                const description = eventData.description || '';
                                const dateStr = eventData.date || 'Próximamente';
                                const timeStr = eventData.time || '10:00 AM';
                                const meetingType = eventData.meetingType || 'video';
                                const locOrLink = eventData.locationOrLink || 'Llamada Zoik';

                                const typeConfig: Record<string, { icon: string; label: string; color: string }> = {
                                  video: { icon: '📹', label: 'Videollamada', color: '#0070f3' },
                                  audio: { icon: '📞', label: 'Llamada de voz', color: '#10b981' },
                                  in_person: { icon: '🏢', label: 'Presencial', color: '#f59e0b' },
                                };
                                const tCfg = typeConfig[meetingType] || typeConfig.video;

                                const dateParts = String(dateStr).split(' ');
                                const month = dateParts.length > 1 ? dateParts[1] : 'EVENTO';
                                const day = dateParts.length > 0 ? dateParts[0] : '📅';

                                return (
                                  <div style={{
                                    width: '100%',
                                    minWidth: '270px',
                                    maxWidth: '340px',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(241, 245, 249, 0.95)',
                                    border: isMe ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(226, 232, 240, 0.9)',
                                    padding: '12px 14px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                      {/* Caja de Fecha */}
                                      <div style={{
                                        width: '44px',
                                        height: '46px',
                                        borderRadius: '10px',
                                        background: isMe ? 'rgba(255,255,255,0.2)' : '#ffffff',
                                        border: isMe ? '1px solid rgba(255,255,255,0.3)' : '1px solid #e2e8f0',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.06)'
                                      }}>
                                        <div style={{ fontSize: '9px', fontWeight: 800, color: isMe ? '#ffffff' : '#0070f3', textTransform: 'uppercase' }}>
                                          {month}
                                        </div>
                                        <div style={{ fontSize: '15px', fontWeight: 900, color: isMe ? '#ffffff' : '#0f172a', lineHeight: 1 }}>
                                          {day}
                                        </div>
                                      </div>

                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          fontSize: '11px',
                                          fontWeight: 700,
                                          color: tCfg.color,
                                          background: isMe ? 'rgba(255,255,255,0.2)' : 'rgba(0,112,243,0.1)',
                                          padding: '2px 6px',
                                          borderRadius: '6px',
                                          marginBottom: '4px'
                                        }}>
                                          <span>{tCfg.icon}</span>
                                          <span>{tCfg.label}</span>
                                        </div>
                                        <div style={{
                                          fontWeight: 800,
                                          fontSize: '14px',
                                          color: isMe ? '#ffffff' : '#0f172a',
                                          lineHeight: 1.25,
                                          wordBreak: 'break-word'
                                        }}>
                                          {title}
                                        </div>
                                        <div style={{
                                          fontSize: '11.5px',
                                          color: isMe ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                                          marginTop: '3px'
                                        }}>
                                          ⏰ {timeStr} • {locOrLink}
                                        </div>
                                      </div>
                                    </div>

                                    {description && (
                                      <div style={{
                                        fontSize: '12px',
                                        color: isMe ? 'rgba(255, 255, 255, 0.9)' : '#475569',
                                        lineHeight: 1.35,
                                        borderLeft: isMe ? '2px solid rgba(255,255,255,0.4)' : '2px solid #0070f3',
                                        paddingLeft: '8px'
                                      }}>
                                        {description}
                                      </div>
                                    )}

                                    {(meetingType === 'video' || meetingType === 'audio') && (
                                      <button
                                        onClick={() => {
                                          const otherUser = active?.otherUser || active?.members?.find((mb: any) => (mb.user?.id || mb.userId) !== myId)?.user;
                                          if (otherUser?.id) {
                                            triggerGlobalCall(otherUser, meetingType === 'video');
                                          } else {
                                            toast(`Reunión programada para el ${dateStr} a las ${timeStr}`);
                                          }
                                        }}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          padding: '8px 12px',
                                          borderRadius: '10px',
                                          background: isMe ? '#ffffff' : '#0070f3',
                                          color: isMe ? '#0070f3' : '#ffffff',
                                          border: 'none',
                                          fontWeight: 700,
                                          fontSize: '12.5px',
                                          cursor: 'pointer',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          transition: 'transform 0.15s ease'
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                                        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                      >
                                        <span>{meetingType === 'video' ? '📹' : '📞'}</span>
                                        <span>Iniciar Reunión</span>
                                      </button>
                                    )}
                                  </div>
                                );
                              }

                              // 4. TARJETA DE CONTACTO
                              if (isCt) {
                                let contactData: any = {};
                                try { contactData = typeof m.content === 'string' ? JSON.parse(m.content) : m.content; } catch {}
                                const name = contactData.name || 'Contacto';
                                const phone = contactData.phone || contactData.phoneNumber || '';
                                const company = contactData.company || '';
                                const initials = String(name).slice(0, 2).toUpperCase();

                                return (
                                  <div style={{
                                    width: '100%',
                                    minWidth: '260px',
                                    maxWidth: '340px',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(241, 245, 249, 0.95)',
                                    border: isMe ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(226, 232, 240, 0.9)',
                                    padding: '12px 14px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        background: isMe ? 'rgba(255,255,255,0.25)' : '#0070f3',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        fontSize: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}>
                                        {initials}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                          fontWeight: 800,
                                          fontSize: '14px',
                                          color: isMe ? '#ffffff' : '#0f172a',
                                          lineHeight: 1.25,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}>
                                          {name}
                                        </div>
                                        {company && (
                                          <div style={{
                                            fontSize: '11.5px',
                                            color: isMe ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                                            marginTop: '1px'
                                          }}>
                                            {company}
                                          </div>
                                        )}
                                        {phone && (
                                          <div style={{
                                            fontSize: '11.5px',
                                            color: isMe ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                                            marginTop: '1px'
                                          }}>
                                            {phone}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {phone && (
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <a
                                          href={`tel:${phone}`}
                                          style={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            padding: '7px 10px',
                                            borderRadius: '8px',
                                            background: isMe ? '#ffffff' : '#0070f3',
                                            color: isMe ? '#0070f3' : '#ffffff',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            textDecoration: 'none',
                                            transition: 'transform 0.15s ease'
                                          }}
                                          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                                          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                        >
                                          <span>📞</span>
                                          <span>Llamar</span>
                                        </a>
                                        <button
                                          onClick={() => {
                                            navigator.clipboard.writeText(phone);
                                            toast.success('Teléfono copiado');
                                          }}
                                          style={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            padding: '7px 10px',
                                            borderRadius: '8px',
                                            background: isMe ? 'rgba(255,255,255,0.2)' : 'rgba(0,112,243,0.1)',
                                            color: isMe ? '#ffffff' : '#0070f3',
                                            border: isMe ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,112,243,0.2)',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s ease'
                                          }}
                                          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                                          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                        >
                                          <span>📋</span>
                                          <span>Copiar</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // 5. TEXTO CONVENCIONAL
                              return (
                                <div>
                                  <div>{renderParsedText(m.content, isMe)}</div>
                                  {(() => {
                                    const urlMatch = m.content.match(/(https?:\/\/[^\s]+)/i);
                                    if (urlMatch) {
                                      return <MessageLinkPreviewCard url={urlMatch[0]} isMe={isMe} />;
                                    }
                                    return null;
                                  })()}
                                </div>
                              );
                            })()}
                            {/* RENDERIZADO DE ARCHIVOS Y ÁLBUM MULTIMEDIA AGRUPADO ESTILO WHATSAPP CON BOTONES DE DESCARGA */}
                            {(() => {
                              if (!m.fileUrl) return null;

                              let items: Array<{ url: string; type: string; name?: string; size?: number; ext?: string }> = [];
                              if (m.fileType === 'media_group' || (typeof m.fileUrl === 'string' && m.fileUrl.startsWith('['))) {
                                try {
                                  items = JSON.parse(m.fileUrl);
                                } catch {
                                  items = [{ url: m.fileUrl, type: m.fileType || 'image', name: m.fileName }];
                                }
                              } else {
                                items = [{ url: m.fileUrl, type: m.fileType || 'image', name: m.fileName }];
                              }

                              if (items.length === 0) return null;

                              const mediaItems = items.filter(it => getFileMeta(it.name || '', it.type).isMedia);
                              const docItems = items.filter(it => !getFileMeta(it.name || '', it.type).isMedia);

                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                                  {/* 1. SECCIÓN DE FOTOS Y VIDEOS EN ÁLBUM WHATSAPP */}
                                  {mediaItems.length > 0 && (
                                    <div style={{ borderRadius: '14px', overflow: 'hidden' }}>
                                      {mediaItems.length === 1 && (() => {
                                        const it = mediaItems[0];
                                        return (
                                          <div style={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', maxWidth: '340px' }}>
                                            {it.type === 'video' ? (
                                              <div style={{ position: 'relative' }}>
                                                <video src={it.url} controls style={{ width: '100%', maxHeight: '300px', display: 'block', borderRadius: '14px' }} />
                                                <button
                                                  onClick={() => downloadFile(it.url, it.name || 'video.mp4')}
                                                  title="Descargar video"
                                                  style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: 'rgba(0, 0, 0, 0.65)',
                                                    backdropFilter: 'blur(6px)',
                                                    border: '1px solid rgba(255,255,255,0.3)',
                                                    color: '#ffffff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    zIndex: 5,
                                                    transition: 'transform 0.15s ease'
                                                  }}
                                                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                  <IconDownload size={15} color="#ffffff" />
                                                </button>
                                              </div>
                                            ) : (
                                              <div
                                                onClick={() => openGallery(mediaItems, 0)}
                                                style={{ position: 'relative', cursor: 'zoom-in', borderRadius: '14px', overflow: 'hidden' }}
                                              >
                                                <img src={it.url} alt="" style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover', display: 'block', borderRadius: '14px' }} />
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadFile(it.url, it.name || 'imagen.jpg');
                                                  }}
                                                  title="Descargar imagen"
                                                  style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                    backdropFilter: 'blur(6px)',
                                                    border: '1px solid rgba(255,255,255,0.3)',
                                                    color: '#ffffff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    zIndex: 5,
                                                    transition: 'transform 0.15s ease'
                                                  }}
                                                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                  <IconDownload size={15} color="#ffffff" />
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}

                                      {mediaItems.length === 2 && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', maxWidth: '340px' }}>
                                          {mediaItems.map((it, idx) => (
                                            <div
                                              key={idx}
                                              onClick={() => openGallery(mediaItems, idx)}
                                              style={{ position: 'relative', aspectRatio: '1', cursor: 'pointer', overflow: 'hidden', borderRadius: '10px', background: '#07162c' }}
                                            >
                                              {it.type === 'video' ? (
                                                <>
                                                  <video src={it.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                                                    <span style={{ fontSize: '20px' }}>▶️</span>
                                                  </div>
                                                </>
                                              ) : (
                                                <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                              )}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  downloadFile(it.url, it.name);
                                                }}
                                                title="Descargar"
                                                style={{
                                                  position: 'absolute',
                                                  bottom: '6px',
                                                  right: '6px',
                                                  width: '28px',
                                                  height: '28px',
                                                  borderRadius: '50%',
                                                  background: 'rgba(0, 0, 0, 0.65)',
                                                  backdropFilter: 'blur(4px)',
                                                  border: '1px solid rgba(255,255,255,0.3)',
                                                  color: '#ffffff',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  zIndex: 5
                                                }}
                                              >
                                                <IconDownload size={14} color="#ffffff" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {mediaItems.length === 3 && (
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: '1.2fr 1fr',
                                          gridTemplateRows: 'repeat(2, 1fr)',
                                          gap: '4px',
                                          maxWidth: '340px',
                                          height: '240px'
                                        }}>
                                          <div
                                            onClick={() => openGallery(mediaItems, 0)}
                                            style={{ position: 'relative', gridRow: 'span 2', cursor: 'pointer', overflow: 'hidden', borderRadius: '10px', background: '#07162c' }}
                                          >
                                            {mediaItems[0].type === 'video' ? (
                                              <>
                                                <video src={mediaItems[0].url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                                                  <span style={{ fontSize: '24px' }}>▶️</span>
                                                </div>
                                              </>
                                            ) : (
                                              <img src={mediaItems[0].url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                downloadFile(mediaItems[0].url, mediaItems[0].name);
                                              }}
                                              title="Descargar"
                                              style={{
                                                position: 'absolute',
                                                bottom: '6px',
                                                right: '6px',
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '50%',
                                                background: 'rgba(0, 0, 0, 0.65)',
                                                backdropFilter: 'blur(4px)',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                color: '#ffffff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                zIndex: 5
                                              }}
                                            >
                                              <IconDownload size={14} color="#ffffff" />
                                            </button>
                                          </div>
                                          {mediaItems.slice(1).map((it, idx) => (
                                            <div
                                              key={idx + 1}
                                              onClick={() => openGallery(mediaItems, idx + 1)}
                                              style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden', borderRadius: '10px', background: '#07162c' }}
                                            >
                                              {it.type === 'video' ? (
                                                <>
                                                  <video src={it.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                                                    <span style={{ fontSize: '18px' }}>▶️</span>
                                                  </div>
                                                </>
                                              ) : (
                                                <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                              )}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  downloadFile(it.url, it.name);
                                                }}
                                                title="Descargar"
                                                style={{
                                                  position: 'absolute',
                                                  bottom: '6px',
                                                  right: '6px',
                                                  width: '26px',
                                                  height: '26px',
                                                  borderRadius: '50%',
                                                  background: 'rgba(0, 0, 0, 0.65)',
                                                  backdropFilter: 'blur(4px)',
                                                  border: '1px solid rgba(255,255,255,0.3)',
                                                  color: '#ffffff',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  zIndex: 5
                                                }}
                                              >
                                                <IconDownload size={13} color="#ffffff" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {mediaItems.length >= 4 && (() => {
                                        const displayItems = mediaItems.slice(0, 4);
                                        const extraCount = mediaItems.length - 4;
                                        return (
                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', maxWidth: '340px' }}>
                                            {displayItems.map((it, idx) => {
                                              const isLastAndExtra = idx === 3 && extraCount > 0;
                                              return (
                                                <div
                                                  key={idx}
                                                  onClick={() => openGallery(mediaItems, idx)}
                                                  style={{ position: 'relative', aspectRatio: '1', cursor: 'pointer', overflow: 'hidden', borderRadius: '10px', background: '#07162c' }}
                                                >
                                                  {it.type === 'video' ? (
                                                    <video src={it.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                  ) : (
                                                    <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                  )}

                                                  {it.type === 'video' && !isLastAndExtra && (
                                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                                                      <span style={{ fontSize: '20px' }}>▶️</span>
                                                    </div>
                                                  )}

                                                  {isLastAndExtra ? (
                                                    <div style={{
                                                      position: 'absolute',
                                                      inset: 0,
                                                      background: 'rgba(0, 0, 0, 0.65)',
                                                      backdropFilter: 'blur(4px)',
                                                      WebkitBackdropFilter: 'blur(4px)',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      color: '#ffffff',
                                                      fontSize: '22px',
                                                      fontWeight: 900
                                                    }}>
                                                      +{extraCount}
                                                    </div>
                                                  ) : (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadFile(it.url, it.name);
                                                      }}
                                                      title="Descargar"
                                                      style={{
                                                        position: 'absolute',
                                                        bottom: '6px',
                                                        right: '6px',
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '50%',
                                                        background: 'rgba(0, 0, 0, 0.65)',
                                                        backdropFilter: 'blur(4px)',
                                                        border: '1px solid rgba(255,255,255,0.3)',
                                                        color: '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        zIndex: 5
                                                      }}
                                                    >
                                                      <IconDownload size={14} color="#ffffff" />
                                                    </button>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* 2. SECCIÓN DE DOCUMENTOS, AUDIOS Y ARCHIVOS EN TARJETAS WHATSAPP */}
                                  {docItems.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', maxWidth: '340px' }}>
                                      {docItems.map((doc, dIdx) => {
                                        const meta = getFileMeta(doc.name || '', doc.type);
                                        if (doc.type === 'audio') {
                                          return (
                                            <WebVoiceNotePlayer
                                              key={dIdx}
                                              url={doc.url}
                                              name={doc.name || 'Nota de voz'}
                                              isMe={isMe}
                                            />
                                          );
                                        }

                                        return (
                                          <div
                                            key={dIdx}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: '10px',
                                              padding: '9px 12px',
                                              borderRadius: '14px',
                                              background: isMe ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.92)',
                                              border: isMe ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(203, 213, 225, 0.85)',
                                              boxShadow: isMe ? '0 2px 8px rgba(0,0,0,0.1)' : '0 2px 8px rgba(15, 42, 74, 0.05)',
                                              backdropFilter: 'blur(10px)',
                                              WebkitBackdropFilter: 'blur(10px)',
                                              transition: 'all 0.15s ease'
                                            }}
                                          >
                                            {/* Icono y Badge de Extensión */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                                              <div style={{
                                                width: '38px',
                                                height: '38px',
                                                borderRadius: '10px',
                                                background: isMe ? 'rgba(255, 255, 255, 0.2)' : `${meta.color}15`,
                                                border: `1.5px solid ${isMe ? 'rgba(255,255,255,0.4)' : meta.color}`,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                              }}>
                                                <span style={{ fontSize: '15px', lineHeight: 1 }}>{meta.icon}</span>
                                                <span style={{ fontSize: '7.5px', fontWeight: 900, color: isMe ? '#ffffff' : meta.color, marginTop: '1px', letterSpacing: '0.2px' }}>
                                                  {meta.badge.slice(0, 4)}
                                                </span>
                                              </div>

                                              {/* Nombre y Peso del Archivo */}
                                              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                                                <span
                                                  title={doc.name || 'Archivo adjunto'}
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: 800,
                                                    color: isMe ? '#ffffff' : '#0f2a4a',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {doc.name || 'Archivo adjunto'}
                                                </span>
                                                <span style={{ fontSize: '10px', fontWeight: 600, color: isMe ? 'rgba(255,255,255,0.75)' : '#64748b' }}>
                                                  {formatFileSize(doc.size) || (meta.badge + ' Documento')}
                                                </span>
                                              </div>
                                            </div>

                                            {/* Botón de Descarga Universal */}
                                            <button
                                              onClick={() => downloadFile(doc.url, doc.name)}
                                              title={`Descargar ${doc.name || 'archivo'}`}
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: isMe ? 'rgba(255, 255, 255, 0.28)' : '#0070f3',
                                                color: '#ffffff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                transition: 'transform 0.15s ease'
                                              }}
                                              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.12)'}
                                              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                            >
                                              <IconDownload size={15} color="#ffffff" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                                                        {/* Insignias de Reacciones Agrupadas estilo WhatsApp */}
                            {Array.isArray(m.reactions) && m.reactions.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                {Array.from(new Set(m.reactions.map((r: any) => r.emoji))).map((emoji: any) => {
                                  const count = m.reactions.filter((r: any) => r.emoji === emoji).length;
                                  const reactedByMe = m.reactions.some((r: any) => r.emoji === emoji && r.userId === authUser?.id);
                                  const names = m.reactions.filter((r: any) => r.emoji === emoji).map((r: any) => r.userName).join(', ');
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={() => handleToggleReaction(m.id, emoji)}
                                      title={`Reaccionaron: ${names}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '3px 8px',
                                        borderRadius: '14px',
                                        background: reactedByMe ? (isMe ? 'rgba(255,255,255,0.32)' : 'rgba(0,112,243,0.12)') : (isMe ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.75)'),
                                        backdropFilter: 'blur(8px)',
                                        WebkitBackdropFilter: 'blur(8px)',
                                        border: reactedByMe ? (isMe ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(0,112,243,0.4)') : (isMe ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.9)'),
                                        color: isMe ? '#ffffff' : '#0f2a4a',
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                                      }}
                                    >
                                      <WhatsAppEmoji emoji={emoji} size={15} />
                                      <span>{count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', fontSize: '9px', fontWeight: 700, color: isMe ? 'rgba(255,255,255,0.7)' : 'rgba(15,42,74,0.4)', marginTop: '4px' }}>
                              {(m.isEdited || (m.updatedAt && m.createdAt && new Date(m.updatedAt).getTime() - new Date(m.createdAt).getTime() > 2000)) && (
                                <span style={{ fontStyle: 'italic', opacity: 0.8, fontSize: '8.5px', marginRight: '2px' }}>editado</span>
                              )}
                              <span>{fmtT(m.createdAt)}</span>
                              {isMe && <DoubleCheckIcon size={14} color={isMe ? 'rgba(255,255,255,0.85)' : '#0070f3'} />}
                            </div>
                          </div>
                        );
                      })()}
                      </div>
                    </React.Fragment>
                  );
                })}

                {/* Indicadores de Typing en tiempo real en la corriente de mensajes */}
                {activeTypingUsers.map(tUser => (
                  <div
                    key={tUser.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: '8px',
                      margin: '4px 0 10px 0',
                      animation: 'zoikFadeIn 0.25s ease-out'
                    }}
                  >
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: colorFor(tUser.userName),
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 900,
                      overflow: 'hidden',
                      border: '1.5px solid rgba(255,255,255,0.9)',
                      boxShadow: '0 2px 6px rgba(15, 42, 74, 0.12)',
                      flexShrink: 0
                    }}>
                      {tUser.userAvatar ? <img src={tUser.userAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(tUser.userName)}
                    </div>

                    {/* Burbuja Glassmorphic de Typing (Solo las 3 bolitas rebotando) */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      padding: '12px 18px',
                      borderRadius: '20px 20px 20px 4px',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.8) 100%)',
                      backdropFilter: 'blur(16px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                      border: '1px solid rgba(255, 255, 255, 0.95)',
                      boxShadow: '0 6px 20px rgba(15, 42, 74, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.95)',
                      minHeight: '38px',
                      boxSizing: 'border-box'
                    }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#0070f3', display: 'inline-block', animation: 'zoikTypingBounce 1.2s infinite ease-in-out', animationDelay: '0s' }} />
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#0070f3', display: 'inline-block', animation: 'zoikTypingBounce 1.2s infinite ease-in-out', animationDelay: '0.2s' }} />
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#0070f3', display: 'inline-block', animation: 'zoikTypingBounce 1.2s infinite ease-in-out', animationDelay: '0.4s' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Bandeja de Múltiples Archivos Adjuntos (Carousel / Tray) */}
              {attachments.length > 0 && (
                <div style={{
                  padding: '8px 24px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  animation: 'zoikFadeIn 0.2s ease-out'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#0070f3' }}>
                      <span>📎</span>
                      <span>{attachments.length} archivo{attachments.length > 1 ? 's' : ''} preparado{attachments.length > 1 ? 's' : ''} para enviar con tu mensaje</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          background: 'rgba(0, 112, 243, 0.1)',
                          border: '1px solid rgba(0, 112, 243, 0.3)',
                          color: '#0070f3',
                          fontSize: '10.5px',
                          fontWeight: 800,
                          padding: '3px 9px',
                          borderRadius: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        + Añadir más
                      </button>
                      <button
                        onClick={clearAllAttachments}
                        style={{
                          background: 'rgba(239, 68, 68, 0.08)',
                          border: 'none',
                          color: '#ef4444',
                          fontSize: '10.5px',
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        Quitar todos
                      </button>
                    </div>
                  </div>

                  {/* Barra de Progreso en Vivo para Archivos Pesados */}
                  {uploading && (
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.95) 0%, rgba(219, 234, 254, 0.9) 100%)',
                      border: '1.5px solid rgba(147, 197, 253, 0.8)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                      boxShadow: '0 2px 8px rgba(0, 112, 243, 0.08)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0070f3', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'zoikSpin 0.9s linear infinite' }}>
                            <circle cx="12" cy="12" r="10" stroke="rgba(0,112,243,0.25)" strokeWidth="3" />
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="#0070f3" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                          Subiendo {attachments.length > 1 ? `${attachments.length} archivos` : (attachments[0]?.name || 'archivo')}...
                        </span>
                        <span style={{ fontSize: '11.5px', fontWeight: 900, color: '#0070f3', fontFamily: 'monospace' }}>
                          {uploadProgress ?? 0}%
                        </span>
                      </div>
                      <div style={{ width: '100%', height: '5px', background: 'rgba(0, 112, 243, 0.15)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.max(4, uploadProgress ?? 0)}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #0070f3 0%, #00c6ff 100%)',
                          borderRadius: '6px',
                          transition: 'width 0.2s ease'
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Scroll Horizontal de Adjuntos */}
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    overflowX: 'auto',
                    paddingBottom: '6px',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {attachments.map(att => {
                      const meta = getFileMeta(att.name, att.type);
                      return (
                        <div
                          key={att.id}
                          style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px 8px 8px',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 100%)',
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                            border: '1.5px solid rgba(191, 219, 254, 0.8)',
                            borderRadius: '14px',
                            boxShadow: '0 4px 14px rgba(15, 42, 74, 0.08)',
                            minWidth: '180px',
                            maxWidth: '220px',
                            flexShrink: 0
                          }}
                        >
                          {att.type === 'image' ? (
                            <img src={att.url} alt="" style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(0,112,243,0.2)' }} />
                          ) : att.type === 'video' ? (
                            <div style={{ width: '42px', height: '42px', borderRadius: '8px', background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                              🎥
                            </div>
                          ) : att.type === 'audio' ? (
                            <div style={{ width: '42px', height: '42px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                              🎵
                            </div>
                          ) : (
                            <div style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '8px',
                              background: `${meta.color}15`,
                              border: `1px solid ${meta.color}40`,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <span style={{ fontSize: '16px', lineHeight: 1 }}>{meta.icon}</span>
                              <span style={{ fontSize: '8px', fontWeight: 900, color: meta.color, marginTop: '2px' }}>{meta.badge.slice(0, 4)}</span>
                            </div>
                          )}

                          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
                            <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f2a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {att.name}
                            </span>
                            <span style={{ fontSize: '9.5px', fontWeight: 600, color: '#0070f3' }}>
                              {formatFileSize(att.size) || meta.badge}
                            </span>
                          </div>

                          <button
                            onClick={() => removeAttachment(att.id)}
                            title="Quitar este archivo"
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              border: 'none',
                              background: 'rgba(15, 42, 74, 0.08)',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 900,
                              flexShrink: 0,
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#ffffff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15, 42, 74, 0.08)'; e.currentTarget.style.color = '#64748b'; }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Barra de Entrada Flotante en Glassmorphism */}
              <div
                className="chat-input-pad"
                style={{
                  padding: '10px 20px 16px',
                  background: 'linear-gradient(180deg, rgba(237, 244, 252, 0.5) 0%, rgba(255, 255, 255, 0.95) 100%)',
                  borderTop: '1px solid rgba(191, 219, 254, 0.5)',
                  position: 'relative',
                  zIndex: 50,
                  boxSizing: 'border-box',
                  width: '100%',
                  maxWidth: '100%'
                }}
              >
                {/* PREVISUALIZACIÓN DE RESPUESTA A MENSAJE (ESTILO WHATSAPP / LIQUID GLASS) */}
                {replyTo && (
                  <div style={{
                    marginBottom: '10px',
                    padding: '8px 14px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 248, 255, 0.92) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(191, 219, 254, 0.8)',
                    borderLeft: '4px solid #0070f3',
                    boxShadow: '0 4px 16px rgba(15, 42, 74, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    animation: 'zoikDrawerSlideUp 0.18s ease-out'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#0070f3', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <IconReply size={16} />
                      </div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#0070f3' }}>
                          Respondiendo a {replyTo.sender?.firstName || 'Usuario'} {replyTo.sender?.lastName || ''}
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {replyTo.fileType === 'image' ? '📷 Foto' :
                           replyTo.fileType === 'video' ? '🎥 Video' :
                           replyTo.fileType === 'audio' ? '🎤 Nota de voz' :
                           replyTo.fileType === 'sticker' ? '🎭 Sticker' :
                           replyTo.fileType === 'media_group' ? '📁 Álbum multimedia' :
                           (replyTo.content || replyTo.fileName || 'Mensaje')}
                        </div>
                      </div>
                    </div>
                    {replyTo.fileUrl && (replyTo.fileType === 'image' || replyTo.fileType === 'sticker') && (
                      <img
                        src={replyTo.fileUrl}
                        alt=""
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <button
                      onClick={() => setReplyTo(null)}
                      title="Cancelar respuesta"
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 800,
                        flexShrink: 0
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                <div
                  className="chat-input-bubble"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(245, 250, 255, 0.85) 100%)',
                    backdropFilter: 'blur(16px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                    border: '1px solid rgba(255, 255, 255, 0.95)',
                    borderRadius: '30px',
                    boxShadow: '0 8px 30px rgba(15, 42, 74, 0.08), inset 0 1px 2px rgba(255,255,255,0.9)',
                    padding: '4px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    position: 'relative',
                    zIndex: 50,
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0
                  }}
                >
                  {isRec ? (
                    /* ESTUDIO DE GRABACIÓN DE AUDIO ACTIVO (WHATSAPP STYLE) */
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '2px 4px',
                      width: '100%',
                      minWidth: 0,
                      animation: 'zoikFadeIn 0.2s ease'
                    }}>
                      {/* Indicador de Grabación Rojo Pulsante y Tiempo */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: '#ef4444',
                          boxShadow: '0 0 10px #ef4444',
                          animation: 'zoikRecordPulse 1s infinite alternate'
                        }} />
                        <span style={{ fontSize: '13px', fontWeight: 800, color: '#ef4444', fontFamily: 'monospace' }}>
                          {Math.floor(recT / 60)}:{(recT % 60) < 10 ? '0' : ''}{recT % 60}
                        </span>
                      </div>

                      {/* Simulación Dinámica de Ondas de Voz */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flex: 1, justifyContent: 'center', height: '24px', overflow: 'hidden', minWidth: 0 }}>
                        {[16, 26, 12, 30, 20, 32, 14, 28, 18, 24, 30, 16, 26, 12].map((h, i) => (
                          <div
                            key={i}
                            style={{
                              width: '3px',
                              height: `${Math.max(6, (h * (((i + (recT % 4) * 2) % 6) + 2)) / 6)}px`,
                              background: 'linear-gradient(180deg, #0070f3, #0056d6)',
                              borderRadius: '3px',
                              transition: 'height 0.15s ease'
                            }}
                          />
                        ))}
                      </div>

                      {/* Acciones de Grabación: Cancelar + Enviar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={cancelRec}
                          title="Descartar nota de voz"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            borderRadius: '16px',
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#dc2626',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = '#fee2e2';
                            e.currentTarget.style.transform = 'scale(1.04)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <IconTrash size={14} color="#dc2626" />
                          <span>Cancelar</span>
                        </button>

                        <button
                          className="chat-send-btn"
                          onClick={stopRec}
                          title="Enviar Nota de Voz"
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(0, 112, 243, 0.35)',
                            flexShrink: 0,
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <IconSend size={16} color="#ffffff" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Botón Adjuntar Archivo */}
                      <button
                        className="chat-action-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Adjuntar Archivo"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'rgba(15, 42, 74, 0.55)',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#0070f3'; e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(15, 42, 74, 0.55)'; e.currentTarget.style.background = 'none'; }}
                      >
                        <IconPaperclip size={19} color="currentColor" />
                      </button>
                      <input ref={fileInputRef} type="file" multiple accept="*/*" style={{ display: 'none' }} onChange={handleFilesSelect} />

                      {/* Textarea */}
                      <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder="Escribe un mensaje aquí... (Shift + Enter para salto de línea)"
                        value={input}
                        onChange={handleInputChange}
                        onPaste={handlePaste}
                        onKeyDown={e => {
                          if (mentionQuery !== null && mentionCandidates.length > 0) {
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              setMentionIndex(prev => (prev + 1) % mentionCandidates.length);
                              return;
                            }
                            if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              setMentionIndex(prev => (prev - 1 + mentionCandidates.length) % mentionCandidates.length);
                              return;
                            }
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              const selected = mentionCandidates[mentionIndex] || mentionCandidates[0];
                              if (selected) insertMention(selected);
                              return;
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setMentionQuery(null);
                              return;
                            }
                          }

                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMsg();
                            const target = e.target as HTMLTextAreaElement;
                            if (target) target.style.height = 'auto';
                          }
                        }}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#0f2a4a',
                          background: 'transparent',
                          resize: 'none',
                          maxHeight: '120px',
                          padding: '8px 4px',
                          fontFamily: '"Montserrat", sans-serif',
                          minWidth: '60px'
                        }}
                      />

                      {/* POPUP FLOTANTE DE AUTOCOMPLETADO DE MENCIONES CON @ */}
                      {mentionQuery !== null && mentionCandidates.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 'calc(100% + 12px)',
                            left: '12px',
                            width: '310px',
                            maxHeight: '260px',
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1.5px solid rgba(0, 112, 243, 0.25)',
                            borderRadius: '18px',
                            boxShadow: '0 16px 40px rgba(15, 42, 74, 0.22), 0 2px 6px rgba(0,0,0,0.05)',
                            zIndex: 1000,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            animation: 'zoikDrawerSlideUp 0.18s cubic-bezier(0.16, 1, 0.3, 1)'
                          }}
                        >
                          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0, 112, 243, 0.04)' }}>
                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#0070f3', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span>@</span> Mencionar Miembro
                            </span>
                            <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
                              ↑↓ o Enter para elegir
                            </span>
                          </div>
                          <div style={{ overflowY: 'auto', padding: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {mentionCandidates.map((u, i) => {
                              const isSelected = i === mentionIndex;
                              const memberName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Usuario';
                              return (
                                <div
                                  key={u.id}
                                  onClick={() => insertMention(u)}
                                  onMouseEnter={() => setMentionIndex(i)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 10px',
                                    borderRadius: '12px',
                                    background: isSelected ? 'rgba(0, 112, 243, 0.12)' : 'transparent',
                                    border: isSelected ? '1px solid rgba(0, 112, 243, 0.25)' : '1px solid transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.12s ease'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: colorFor(memberName), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, overflow: 'hidden', flexShrink: 0 }}>
                                      {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(memberName)}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                      <span style={{ fontSize: '12px', fontWeight: isSelected ? 800 : 700, color: isSelected ? '#0070f3' : '#0f2a4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {memberName}
                                      </span>
                                      <span style={{ fontSize: '9.5px', color: isSelected ? '#0056d6' : '#64748b', fontWeight: 600 }}>
                                        {u.role || 'Miembro'}
                                      </span>
                                    </div>
                                  </div>
                                  <span style={{ fontSize: '10px', color: isSelected ? '#0070f3' : 'rgba(15, 42, 74, 0.35)', fontWeight: 800 }}>
                                    @{u.firstName || 'user'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* BARRA DESPLEGABLE FLOTANTE UNIFICADA DE EMOJIS Y STICKERS */}
                      {showPicker && (
                        <div style={{
                          position: 'absolute',
                          bottom: 'calc(100% + 12px)',
                          left: '0',
                          right: '0',
                          width: '100%',
                          height: '340px',
                          background: 'rgba(255, 255, 255, 0.98)',
                          backdropFilter: 'blur(20px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                          border: '1.5px solid rgba(203, 213, 225, 0.95)',
                          borderRadius: '20px',
                          boxShadow: '0 16px 40px rgba(15, 42, 74, 0.22)',
                          zIndex: 999,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          animation: 'zoikDrawerSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}>
                          {/* HEADER TABS & ACCIONES */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 14px',
                            background: 'rgba(248, 250, 252, 0.95)',
                            borderBottom: '1px solid #e2e8f0',
                            flexShrink: 0
                          }}>
                            {/* PESTAÑAS */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button
                                onClick={() => setPickerTab('emoji')}
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '14px',
                                  border: 'none',
                                  background: pickerTab === 'emoji' ? '#0070f3' : 'transparent',
                                  color: pickerTab === 'emoji' ? '#ffffff' : '#475569',
                                  fontSize: '12px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <span>😀 Emojis</span>
                              </button>
                              <button
                                onClick={() => setPickerTab('stickers')}
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '14px',
                                  border: 'none',
                                  background: pickerTab === 'stickers' ? '#0070f3' : 'transparent',
                                  color: pickerTab === 'stickers' ? '#ffffff' : '#475569',
                                  fontSize: '12px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <span>✨ Stickers</span>
                                {stickers.length > 0 && (
                                  <span style={{
                                    fontSize: '10px',
                                    padding: '1px 6px',
                                    borderRadius: '10px',
                                    background: pickerTab === 'stickers' ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
                                    color: pickerTab === 'stickers' ? '#ffffff' : '#0f2a4a'
                                  }}>
                                    {stickers.length}
                                  </span>
                                )}
                              </button>
                            </div>

                            {/* ACCIONES SUPERIORES */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {pickerTab === 'stickers' && (
                                <>
                                  <button
                                    onClick={() => stickerInputRef.current?.click()}
                                    disabled={uploadingSticker}
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '10px',
                                      border: '1px solid #bfdbfe',
                                      background: '#eff6ff',
                                      color: '#0070f3',
                                      fontSize: '11px',
                                      fontWeight: 800,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      transition: 'all 0.15s ease'
                                    }}
                                  >
                                    <span>+ Subir Sticker</span>
                                  </button>
                                  <input
                                    ref={stickerInputRef}
                                    type="file"
                                    accept="image/png,image/webp,image/gif,image/jpeg"
                                    style={{ display: 'none' }}
                                    onChange={handleUploadSticker}
                                  />
                                </>
                              )}

                              <button
                                onClick={() => setShowPicker(false)}
                                title="Cerrar"
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#94a3b8',
                                  fontSize: '14px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f2a4a'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          {/* CUERPO DEL PANEL */}
                          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {pickerTab === 'emoji' ? (
                              <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                                <EmojiPicker
                                  emojiStyle={EmojiStyle.APPLE}
                                  theme={Theme.LIGHT}
                                  width="100%"
                                  height="100%"
                                  searchPlaceHolder="Buscar emoji en WhatsApp..."
                                  searchDisabled={false}
                                  skinTonesDisabled={false}
                                  previewConfig={{ showPreview: false }}
                                  lazyLoadEmojis={true}
                                  onEmojiClick={(emojiData) => {
                                    setInput(prev => prev + emojiData.emoji);
                                    textareaRef.current?.focus();
                                  }}
                                />
                              </div>
                            ) : (
                              /* PESTAÑA STICKERS */
                              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
                                {stickers.length === 0 ? (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '16px 8px',
                                    textAlign: 'center'
                                  }}>
                                    <div style={{ fontSize: '30px', marginBottom: '4px' }}>🎭</div>
                                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a', marginBottom: '4px' }}>
                                      Aún no tienes stickers guardados
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', maxWidth: '380px', marginBottom: '12px', lineHeight: 1.4 }}>
                                      Sube tus imágenes o GIFs favoritos, o haz clic en la estrella ⭐ en cualquier sticker del chat para guardarlo en tu colección.
                                    </div>
                                    <button
                                      onClick={() => stickerInputRef.current?.click()}
                                      disabled={uploadingSticker}
                                      style={{
                                        padding: '7px 16px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        boxShadow: 'none'
                                      }}
                                    >
                                      {uploadingSticker ? 'Subiendo...' : '+ Subir Mi Primer Sticker'}
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
                                    gap: '10px'
                                  }}>
                                    {stickers.map(stk => (
                                      <div
                                        key={stk.id}
                                        onClick={() => {
                                          sendMsg({ fileUrl: stk.url, fileType: 'sticker', fileName: stk.name || 'Sticker' });
                                          setShowPicker(false);
                                        }}
                                        style={{
                                          height: '76px',
                                          borderRadius: '12px',
                                          padding: '6px',
                                          background: '#f8fafc',
                                          border: '1.5px solid #e2e8f0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          transition: 'all 0.15s ease',
                                          position: 'relative'
                                        }}
                                        onMouseEnter={e => {
                                          e.currentTarget.style.borderColor = '#0070f3';
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 112, 243, 0.2)';
                                        }}
                                        onMouseLeave={e => {
                                          e.currentTarget.style.borderColor = '#e2e8f0';
                                          e.currentTarget.style.transform = 'scale(1)';
                                          e.currentTarget.style.boxShadow = 'none';
                                        }}
                                      >
                                        <img src={stk.url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Botón Selector de Emojis */}
                      <button
                        className="chat-action-btn"
                        onClick={() => {
                          if (showPicker && pickerTab === 'emoji') {
                            setShowPicker(false);
                          } else {
                            setPickerTab('emoji');
                            setShowPicker(true);
                          }
                        }}
                        title="Emojis"
                        style={{
                          background: (showPicker && pickerTab === 'emoji') ? 'rgba(0, 112, 243, 0.12)' : 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '50%',
                          color: (showPicker && pickerTab === 'emoji') ? '#0070f3' : 'rgba(15, 42, 74, 0.55)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => { if (!showPicker || pickerTab !== 'emoji') { e.currentTarget.style.color = '#0070f3'; e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)'; } }}
                        onMouseLeave={e => { if (!showPicker || pickerTab !== 'emoji') { e.currentTarget.style.color = 'rgba(15, 42, 74, 0.55)'; e.currentTarget.style.background = 'none'; } }}
                      >
                        <IconSmile size={19} color="currentColor" />
                      </button>

                      {/* Botón Selector de Stickers */}
                      <button
                        className="chat-action-btn"
                        onClick={() => {
                          if (showPicker && pickerTab === 'stickers') {
                            setShowPicker(false);
                          } else {
                            setPickerTab('stickers');
                            setShowPicker(true);
                          }
                        }}
                        title="Stickers"
                        style={{
                          background: (showPicker && pickerTab === 'stickers') ? 'rgba(0, 112, 243, 0.12)' : 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '50%',
                          color: (showPicker && pickerTab === 'stickers') ? '#0070f3' : 'rgba(15, 42, 74, 0.55)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => { if (!showPicker || pickerTab !== 'stickers') { e.currentTarget.style.color = '#0070f3'; e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)'; } }}
                        onMouseLeave={e => { if (!showPicker || pickerTab !== 'stickers') { e.currentTarget.style.color = 'rgba(15, 42, 74, 0.55)'; e.currentTarget.style.background = 'none'; } }}
                      >
                        <IconSticker size={19} color="currentColor" />
                      </button>

                      {/* Botón Micrófono */}
                      <button
                        className="chat-action-btn"
                        onClick={startRec}
                        title="Grabar Nota de Voz"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'rgba(15, 42, 74, 0.55)',
                          padding: '6px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#0070f3'; e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(15, 42, 74, 0.55)'; e.currentTarget.style.background = 'none'; }}
                      >
                        <IconMic size={19} color="currentColor" />
                      </button>

                      {/* Botón Enviar Mensaje */}
                      {(() => {
                        const canSend = !sending && !uploading && (Boolean(input.trim()) || Boolean(preview) || attachments.length > 0);
                        return (
                          <button
                            className="chat-send-btn"
                            onClick={() => sendMsg()}
                            disabled={!canSend}
                            title={uploading ? `Subiendo archivo... (${uploadProgress ?? 0}%)` : (canSend ? 'Enviar Mensaje' : 'Escribe un mensaje o adjunta un archivo')}
                            style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '50%',
                              border: 'none',
                              background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: canSend ? 'pointer' : 'default',
                              boxShadow: 'none',
                              opacity: canSend ? 1 : 0.55,
                              flexShrink: 0,
                              overflow: 'hidden',
                              position: 'relative',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              if (canSend) {
                                (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(0, 112, 243, 0.35)';
                              }
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                            }}
                            onMouseDown={e => {
                              if (canSend) {
                                (e.currentTarget as HTMLElement).style.transform = 'scale(0.95)';
                              }
                            }}
                            onMouseUp={e => {
                              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                            }}
                          >
                            {uploading ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'zoikSpin 0.9s linear infinite' }}>
                                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                                {uploadProgress !== null && uploadProgress > 0 && (
                                  <span style={{ fontSize: '7.5px', fontWeight: 900, color: '#ffffff', marginTop: '1px', lineHeight: 1 }}>
                                    {uploadProgress}%
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  animation: isFlying ? 'zoikPlaneTakeoff 0.75s cubic-bezier(0.34, 1.3, 0.64, 1)' : 'none',
                                  transform: isFlying ? undefined : 'translate(-1px, 1px)'
                                }}
                              >
                                <IconSend size={16} color="#ffffff" />
                              </span>
                            )}
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>

              {/* BOTÓN FLOTANTE DE SCROLL RÁPIDO AL ÚLTIMO MENSAJE (ESTILO WHATSAPP / LIQUID GLASS) */}
              {showScrollBottom && (
                <button
                  onClick={() => scrollToBottom(true)}
                  title="Ir al último mensaje"
                  aria-label="Ir al último mensaje"
                  style={{
                    position: 'absolute',
                    bottom: (attachments.length > 0 || replyTo) ? '155px' : '95px',
                    right: '26px',
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(240, 248, 255, 0.92) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1.5px solid rgba(191, 219, 254, 0.9)',
                    boxShadow: '0 8px 24px rgba(15, 42, 74, 0.16), 0 2px 6px rgba(0, 0, 0, 0.05)',
                    color: '#0070f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 60,
                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    animation: 'zoikFadeIn 0.2s ease-out'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.12) translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 28px rgba(0, 112, 243, 0.3), 0 3px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1) translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(15, 42, 74, 0.16), 0 2px 6px rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(240, 248, 255, 0.92) 100%)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0070f3" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              )}
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(15,42,74,0.4)', fontWeight: 700 }}>Selecciona una conversación para comenzar a chatear.</div>
          )}
        </div>

      </div>

      {/* MODAL INICIAR NUEVO CHAT DIRECTO */}
      {showNewChatModal && (
        <div
          onClick={() => {
            setShowNewChatModal(false);
            setSearchMember('');
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.55)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            animation: 'zoikFadeIn 0.2s ease-out'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              width: '100%',
              maxWidth: '460px',
              boxShadow: '0 25px 60px rgba(15, 42, 74, 0.25)',
              border: '1px solid rgba(15, 42, 74, 0.08)',
              overflow: 'hidden',
              fontFamily: '"Montserrat", sans-serif',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '85vh',
              animation: 'zoikModalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {/* Cabecera */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(0, 112, 243, 0.1)', color: '#0070f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <line x1="12" y1="8" x2="12" y2="14" />
                    <line x1="9" y1="11" x2="15" y2="11" />
                  </svg>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#0f2a4a' }}>Nuevo Chat</h3>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Selecciona un miembro del equipo</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNewChatModal(false);
                  setSearchMember('');
                }}
                style={{
                  background: 'rgba(15, 42, 74, 0.06)',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Buscador */}
            <div style={{ padding: '16px 24px 8px' }}>
              <input
                type="text"
                autoFocus
                placeholder="Buscar por nombre o cargo..."
                value={searchMember}
                onChange={e => setSearchMember(e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  borderRadius: '14px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: 600,
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: '#f8fafc'
                }}
              />
            </div>

            {/* Lista de Miembros */}
            <div style={{ padding: '12px 24px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {team
                .filter(u => u.id !== myId && (!focusMode || focusAllowedUserIds.includes(u.id)))
                .filter(u => {
                  if (!searchMember.trim()) return true;
                  const full = `${u.firstName} ${u.lastName} ${u.role || ''}`.toLowerCase();
                  return full.includes(searchMember.toLowerCase());
                })
                .map(t => {
                  const memberName = `${t.firstName} ${t.lastName}`;
                  return (
                    <div
                      key={t.id}
                      onClick={() => {
                        openDirect(t);
                        setShowNewChatModal(false);
                        setSearchMember('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        borderRadius: '16px',
                        border: '1px solid #f1f5f9',
                        background: '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#f0f7ff';
                        e.currentTarget.style.borderColor = '#bfdbfe';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#f1f5f9';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '50%',
                            background: colorFor(memberName),
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: 900,
                            overflow: 'hidden',
                            flexShrink: 0
                          }}
                        >
                          {t.avatar ? (
                            <img src={t.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            ini(memberName)
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a' }}>{memberName}</div>
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>{t.role || 'Miembro'}</div>
                        </div>
                      </div>

                      <button
                        style={{
                          padding: '6px 14px',
                          borderRadius: '12px',
                          border: 'none',
                          background: '#0070f3',
                          color: '#ffffff',
                          fontSize: '11px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          pointerEvents: 'none'
                        }}
                      >
                        + Chat
                      </button>
                    </div>
                  );
                })}
            </div>

          </div>
        </div>
      )}

      {/* MODAL CREAR GRUPO */}
      {showGrp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(5, 20, 50, 0.75)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowGrp(false)}>
          <div style={{ background: '#ffffff', borderRadius: '20px', width: '100%', maxWidth: '440px', padding: '24px', border: '1.5px solid #cbd5e1', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>Crear Nuevo Grupo o Canal</h3>
            <input type="text" placeholder="Nombre del grupo (ej: Equipo Edwcar)" value={grpName} onChange={e => setGrpName(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: 600, outline: 'none', marginBottom: '14px', boxSizing: 'border-box' }} />
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', marginBottom: '8px' }}>Selecciona Miembros:</div>
            <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' }}>
              {team.filter(u => u.id !== myId).map(u => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
                  <input type="checkbox" checked={selM.includes(u.id)} onChange={e => { if (e.target.checked) setSelM(prev => [...prev, u.id]); else setSelM(prev => prev.filter(id => id !== u.id)); }} />
                  {u.firstName} {u.lastName} ({u.role})
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowGrp(false)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', fontWeight: 800, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={createGroup} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: '#0070f3', color: 'white', fontWeight: 800, cursor: 'pointer' }}>Crear Grupo</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MODO CONCENTRACIÓN EN EL TRABAJO */}
      {showFocusModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(5, 20, 50, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowFocusModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              width: '100%',
              maxWidth: '480px',
              padding: '26px',
              border: '1.5px solid rgba(79, 70, 229, 0.25)',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '85vh'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)'
                }}>
                  🧘
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>
                    Modo Concentración
                  </h3>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                    Trabajo enfocado sin interrupciones
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowFocusModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#f1f5f9',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  fontSize: '16px',
                  fontWeight: 800
                }}
              >
                ✕
              </button>
            </div>

            {/* Switch Principal Activar / Desactivar */}
            <div style={{
              padding: '14px 16px',
              borderRadius: '16px',
              background: focusMode ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)' : '#f8fafc',
              border: focusMode ? '1.5px solid rgba(79, 70, 229, 0.3)' : '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
              transition: 'all 0.2s ease'
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 800, color: focusMode ? '#4338ca' : '#0f2a4a' }}>
                  {focusMode ? 'Modo Concentración ACTIVADO' : 'Activar Modo Concentración'}
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginTop: '2px' }}>
                  {focusMode ? 'Silenciando mensajes directos no autorizados' : 'Enfócate en tus entregas sin distracciones'}
                </div>
              </div>

              {/* Toggle switch iOS style */}
              <div
                onClick={() => handleToggleFocusMode()}
                style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  background: focusMode ? '#4f46e5' : '#cbd5e1',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.25s ease',
                  flexShrink: 0
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '3px',
                  left: focusMode ? '25px' : '3px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'left 0.25s ease'
                }} />
              </div>
            </div>

            {/* Tarjeta de Grupos (Siempre Permitidos) */}
            <div style={{
              padding: '10px 14px',
              borderRadius: '12px',
              background: 'rgba(34, 197, 94, 0.08)',
              border: '1px solid rgba(34, 197, 94, 0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '16px' }}>👥</span>
              <div style={{ fontSize: '11.5px', color: '#15803d', fontWeight: 700 }}>
                Todos los grupos de trabajo y marcas siempre te notificarán durante la concentración.
              </div>
            </div>

            {/* Sección de Selección de Contactos Permitidos */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                Personas permitidas por interno ({focusAllowedUserIds.length}):
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    const allIds = team.filter(u => u.id !== myId).map(u => u.id);
                    setFocusAllowedUserIds(allIds);
                    localStorage.setItem('zoik_focus_allowed_users', JSON.stringify(allIds));
                  }}
                  style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: '11px', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                >
                  Permitir todos
                </button>
                <span style={{ color: '#cbd5e1' }}>•</span>
                <button
                  onClick={() => {
                    setFocusAllowedUserIds([]);
                    localStorage.setItem('zoik_focus_allowed_users', JSON.stringify([]));
                  }}
                  style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '11px', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                >
                  Limpiar
                </button>
              </div>
            </div>

            {/* Input para filtrar miembros */}
            <input
              type="text"
              placeholder="Buscar compañero por nombre..."
              value={focusSearch}
              onChange={e => setFocusSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '12px',
                fontWeight: 600,
                outline: 'none',
                marginBottom: '10px',
                boxSizing: 'border-box'
              }}
            />

            {/* Lista de Miembros del Equipo */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              paddingRight: '4px',
              marginBottom: '18px'
            }}>
              {team
                .filter(u => u.id !== myId && (!focusSearch.trim() || `${u.firstName} ${u.lastName}`.toLowerCase().includes(focusSearch.toLowerCase())))
                .map(u => {
                  const isChecked = focusAllowedUserIds.includes(u.id);
                  const fullName = `${u.firstName} ${u.lastName}`;
                  return (
                    <div
                      key={u.id}
                      onClick={() => handleToggleAllowedUser(u.id)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '12px',
                        background: isChecked ? 'rgba(79, 70, 229, 0.06)' : '#f8fafc',
                        border: isChecked ? '1.5px solid rgba(79, 70, 229, 0.35)' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: colorFor(fullName),
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 800,
                          overflow: 'hidden'
                        }}>
                          {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(fullName)}
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>{fullName}</div>
                          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>{u.role || 'Miembro'}</div>
                        </div>
                      </div>

                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                        border: isChecked ? 'none' : '1.5px solid #cbd5e1',
                        background: isChecked ? '#4f46e5' : '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 900
                      }}>
                        {isChecked && '✓'}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Botón de cierre y confirmación */}
            <button
              onClick={() => {
                setShowFocusModal(false);
                if (!focusMode) {
                  handleToggleFocusMode(true);
                } else {
                  toast.success('Configuración de concentración guardada');
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                color: 'white',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
                transition: 'all 0.15s ease'
              }}
            >
              {focusMode ? 'Listo / Guardar' : 'Activar Modo Concentración Ahora'}
            </button>
          </div>
        </div>
      )}

      {/* GALERÍA MULTIMEDIA ESTILO WHATSAPP CON NAVEGACIÓN */}
      {gallery && gallery.items && gallery.items.length > 0 && (
        <div
          onClick={() => setGallery(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(5, 16, 34, 0.95)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            zIndex: 100000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '20px'
          }}
        >
          {/* Cabecera de la Galería */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#ffffff',
              width: '100%',
              maxWidth: '900px',
              margin: '0 auto'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: 800 }}>
                {gallery.index + 1} de {gallery.items.length}
              </span>
              <span style={{ fontSize: '12px', opacity: 0.7, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {gallery.items[gallery.index]?.name || 'Archivo multimedia'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => {
                  const curr = gallery.items[gallery.index];
                  if (curr) downloadFile(curr.url, curr.name);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '6px 14px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <IconDownload size={14} color="#ffffff" /> Descargar
              </button>
              <button
                onClick={() => setGallery(null)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 900,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Visor Principal con Controles Anterior / Siguiente */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '16px 0',
              overflow: 'hidden'
            }}
          >
            {/* Flecha Anterior */}
            {gallery.items.length > 1 && (
              <button
                onClick={() => setGallery(prev => prev ? { ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length } : null)}
                style={{
                  position: 'absolute',
                  left: '12px',
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: 'rgba(15, 42, 74, 0.75)',
                  backdropFilter: 'blur(8px)',
                  color: '#ffffff',
                  fontSize: '20px',
                  fontWeight: 900,
                  cursor: 'pointer',
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.4)'
                }}
              >
                ‹
              </button>
            )}

            {/* Elemento Activo (Foto o Video) */}
            {gallery.items[gallery.index]?.type === 'video' ? (
              <video
                src={gallery.items[gallery.index]?.url}
                controls
                autoPlay
                style={{ maxWidth: '88vw', maxHeight: '72vh', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
              />
            ) : (
              <img
                src={gallery.items[gallery.index]?.url}
                alt=""
                style={{ maxWidth: '88vw', maxHeight: '72vh', objectFit: 'contain', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
              />
            )}

            {/* Flecha Siguiente */}
            {gallery.items.length > 1 && (
              <button
                onClick={() => setGallery(prev => prev ? { ...prev, index: (prev.index + 1) % prev.items.length } : null)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: 'rgba(15, 42, 74, 0.75)',
                  backdropFilter: 'blur(8px)',
                  color: '#ffffff',
                  fontSize: '20px',
                  fontWeight: 900,
                  cursor: 'pointer',
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.4)'
                }}
              >
                ›
              </button>
            )}
          </div>

          {/* Carrusel Inferior de Miniaturas */}
          {gallery.items.length > 1 && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                overflowX: 'auto',
                padding: '8px 0',
                maxWidth: '900px',
                margin: '0 auto',
                width: '100%'
              }}
            >
              {gallery.items.map((it, idx) => (
                <div
                  key={idx}
                  onClick={() => setGallery(prev => prev ? { ...prev, index: idx } : null)}
                  style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: idx === gallery.index ? '2.5px solid #0070f3' : '1px solid rgba(255,255,255,0.2)',
                    opacity: idx === gallery.index ? 1 : 0.5,
                    transform: idx === gallery.index ? 'scale(1.08)' : 'scale(1)',
                    transition: 'all 0.15s ease',
                    flexShrink: 0
                  }}
                >
                  {it.type === 'video' ? (
                    <div style={{ width: '100%', height: '100%', background: '#0b1e36', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      🎬
                    </div>
                  ) : (
                    <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LIGHTBOX COMPATIBILITY */}
      {lightboxUrl && !gallery && (
        <div onClick={() => setLightboxUrl(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(5, 20, 50, 0.92)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={lightboxUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '12px' }} />
        </div>
      )}

    
      {/* MODAL DE GESTIÓN Y OPCIONES DE GRUPO */}
      {showGroupModal && active && active.type === 'GROUP' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,42,74,0.6)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999 }}>
          <div style={{ background: '#ffffff', borderRadius: '20px', width: '520px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: '"Montserrat", sans-serif' }}>
            
            {/* Cabecera Modal */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconSettings size={18} color="#0070f3" /> Opciones del Grupo
              </h3>
              <button onClick={() => setShowGroupModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Foto y Nombre del Grupo */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: colorFor(active.name || active.displayName || 'Grupo'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 900, overflow: 'hidden' }}>
                    {active.groupAvatar || active.avatar ? <img src={active.groupAvatar || active.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(active.name || active.displayName || 'Grupo')}
                  </div>
                  <label style={{ position: 'absolute', bottom: '-2px', right: '-2px', background: '#0f2a4a', color: 'white', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid white' }} title="Cambiar foto del grupo">
                    <IconCamera size={11} color="white" />
                    <input type="file" accept="image/*" onChange={handleGroupAvatarUpload} style={{ display: 'none' }} />
                  </label>
                </div>
                
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase' }}>Nombre del Grupo</label>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <input
                      type="text"
                      value={editingGroupName}
                      onChange={e => setEditingGroupName(e.target.value)}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: 700 }}
                    />
                    <button
                      onClick={handleUpdateGroupName}
                      style={{ background: '#0070f3', color: 'white', border: 'none', borderRadius: '8px', padding: '0 14px', fontWeight: 800, fontSize: '12px', cursor: 'pointer' }}
                    >
                      Guardar
                    </button>
                  </div>
                </div>
              </div>

              {/* Lista de Miembros */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <IconUsers size={15} color="#0070f3" /> Miembros del Grupo ({active.members?.length || 0})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '8px' }}>
                  {active.members?.map((m: any) => {
                    const memberUser = m.user || team.find((t: any) => t.id === (m.userId || m.id)) || {};
                    const isCurrentUser = (memberUser.id || m.userId) === myId;
                    return (
                      <div key={memberUser.id || m.userId || Math.random()} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fafbfc', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#0070f3', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 900, overflow: 'hidden' }}>
                            {memberUser.avatar ? <img src={memberUser.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (memberUser.firstName?.[0] || 'U')}
                          </div>
                          <div>
                            <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                              {memberUser.firstName} {memberUser.lastName} {isCurrentUser && '(Tú)'}
                            </span>
                            <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '6px' }}>
                              ({memberUser.role || 'MIEMBRO'})
                            </span>
                          </div>
                        </div>
                        {!isCurrentUser && (
                          <button
                            onClick={() => handleRemoveMemberFromGroup(memberUser.id || m.userId)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '11px', fontWeight: 800, cursor: 'pointer', padding: '4px 8px' }}
                          >
                            ✕ Quitar
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Añadir Nuevos Miembros */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <IconPlus size={14} color="#10b981" /> Añadir Integrantes del Equipo
                </label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <select
                    multiple
                    value={selectedNewMembers}
                    onChange={e => {
                      const options = Array.from(e.target.selectedOptions, o => o.value);
                      setSelectedNewMembers(options);
                    }}
                    style={{ flex: 1, height: '80px', padding: '6px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '12px' }}
                  >
                    {team.filter((t: any) => !active.members?.some((m: any) => (m.userId || m.user?.id) === t.id)).map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.firstName} {t.lastName} ({t.role})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddMembersToGroup}
                    disabled={selectedNewMembers.length === 0}
                    style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '0 16px', fontWeight: 800, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <IconPlus size={12} /> Añadir
                  </button>
                </div>
              </div>

              {/* Eliminar Grupo Completo */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: 700 }}>Zona de peligro:</span>
                <button
                  onClick={() => { setShowGroupModal(false); handleDeleteConversation(active.id); }}
                  style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <IconTrash size={14} color="#ef4444" /> Eliminar Grupo Completo
                </button>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* MODAL DE EDICIÓN DE MENSAJE GLASSMORPHISM */}
      {editingMsg && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget && !savingEdit) {
              setEditingMsg(null);
              setEditText('');
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.45)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '20px',
            animation: 'zoikFadeIn 0.2s ease-out'
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(240, 246, 255, 0.85) 100%)',
              backdropFilter: 'blur(25px)',
              WebkitBackdropFilter: 'blur(25px)',
              borderRadius: '24px',
              border: '1.5px solid rgba(255, 255, 255, 0.9)',
              width: '100%',
              maxWidth: '480px',
              boxShadow: '0 25px 60px rgba(15, 42, 74, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.6) inset',
              overflow: 'hidden',
              fontFamily: '"Montserrat", sans-serif',
              display: 'flex',
              flexDirection: 'column',
              animation: 'zoikModalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {/* Cabecera Modal */}
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid rgba(15, 42, 74, 0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.4)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '12px',
                    background: 'rgba(0, 112, 243, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0070f3'
                  }}
                >
                  <IconEdit size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#0f2a4a' }}>
                    Editar Mensaje
                  </h3>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                    Tiempo límite: dentro de las 3 horas del envío • Se actualizará en la misma burbuja
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!savingEdit) {
                    setEditingMsg(null);
                    setEditText('');
                  }
                }}
                style={{
                  background: 'rgba(15, 42, 74, 0.06)',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#64748b',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(15, 42, 74, 0.06)';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                ✕
              </button>
            </div>

            {/* Cuerpo del Modal */}
            <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ position: 'relative' }}>
                <textarea
                  autoFocus
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveEditMessage();
                    } else if (e.key === 'Escape' && !savingEdit) {
                      e.preventDefault();
                      setEditingMsg(null);
                      setEditText('');
                    }
                  }}
                  placeholder="Escribe el texto corregido..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '16px',
                    border: '1.5px solid rgba(0, 112, 243, 0.28)',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0f2a4a',
                    fontFamily: '"Montserrat", sans-serif',
                    outline: 'none',
                    resize: 'none',
                    boxShadow: 'inset 0 2px 4px rgba(15, 42, 74, 0.04), 0 0 0 3px rgba(0, 112, 243, 0.05)',
                    boxSizing: 'border-box',
                    lineHeight: '1.5'
                  }}
                  onFocus={e => {
                    e.currentTarget.style.borderColor = '#0070f3';
                    e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(15, 42, 74, 0.04), 0 0 0 4px rgba(0, 112, 243, 0.12)';
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = 'rgba(0, 112, 243, 0.28)';
                    e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(15, 42, 74, 0.04), 0 0 0 3px rgba(0, 112, 243, 0.05)';
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                  💡 <b style={{ color: '#0f2a4a' }}>Enter</b> para guardar • <b style={{ color: '#0f2a4a' }}>Shift+Enter</b> salto de línea
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700 }}>
                  {editText.length} caracteres
                </span>
              </div>
            </div>

            {/* Pie del Modal */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: '1px solid rgba(15, 42, 74, 0.08)',
                background: 'rgba(255, 255, 255, 0.5)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setEditingMsg(null);
                  setEditText('');
                }}
                disabled={savingEdit}
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(15, 42, 74, 0.15)',
                  color: '#475569',
                  borderRadius: '12px',
                  padding: '10px 18px',
                  fontWeight: 800,
                  fontSize: '13px',
                  cursor: savingEdit ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { if (!savingEdit) e.currentTarget.style.background = 'rgba(241, 245, 249, 0.9)'; }}
                onMouseLeave={e => { if (!savingEdit) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'; }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleSaveEditMessage}
                disabled={savingEdit || !editText.trim()}
                style={{
                  background: savingEdit || !editText.trim() ? '#94a3b8' : 'linear-gradient(135deg, #0070f3 0%, #0051b3 100%)',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '12px',
                  padding: '10px 22px',
                  fontWeight: 800,
                  fontSize: '13px',
                  cursor: savingEdit || !editText.trim() ? 'not-allowed' : 'pointer',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  if (!savingEdit && editText.trim()) e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  if (!savingEdit && editText.trim()) e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {savingEdit ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <IconEdit size={14} /> Guardar Cambios
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Chat;
